/* Elite Eleven Access rewards page: all of its scripts in one file, in load order.
   1. Okendo Storefront API client  2. Product colour groups
   3. API page rendering  4. Page controller (starts everything). */

/* ---------- loyalty-storefront-api ---------- */
(() => {
  'use strict';

  const API = 'https://api.okendo.io/v1';
  const PUBLIC_TTL = 5 * 60 * 1000;
  const CUSTOMER_TTL = 15 * 60 * 1000;
  const NO_DATA = 'Your rewards are temporarily unavailable. Please try again.';
  const cacheInvalidators = new Set();

  function clearPrivateCache(storage) {
    try {
      for (const key of Object.keys(storage)) {
        if (key.startsWith('ee-rewards-api:') && key.split(':')[2] !== 'public') storage.removeItem(key);
      }
    } catch { /* Storage is optional. */ }
  }

  class ApiError extends Error {
    constructor(message, { status = 0, retryAt = 0, uncertain = false } = {}) {
      super(message);
      this.name = 'LoyaltyApiError';
      this.status = status;
      this.retryAt = retryAt;
      this.uncertain = uncertain;
    }
  }

  function safeUrl(value, allowedPath) {
    const url = new URL(value, `${API}/`);
    if (url.pathname.startsWith('/loyalty/')) url.pathname = `/v1${url.pathname}`;
    if (url.origin !== 'https://api.okendo.io' || url.pathname !== allowedPath || url.username || url.password || url.hash) {
      throw new ApiError('Invalid rewards page link.');
    }
    return url;
  }

  function createClient(options = {}) {
    const fetcher = options.fetch || window.fetch.bind(window);
    const now = options.now || Date.now;
    const settings = options.settings || (() => JSON.parse(document.getElementById('oke-loyalty-settings')?.textContent || '{}'));
    const customer = options.customer || (() => JSON.parse(document.getElementById('oke-loyalty-customer')?.textContent || '{}'));
    const locale = options.locale || window.Shopify?.locale || 'en';
    const country = options.country || window.Shopify?.country || 'AU';
    const currency = options.currency || window.Shopify?.currency?.active || 'AUD';
    let storage;
    try { storage = options.storage ?? window.sessionStorage; } catch { storage = null; }
    const pending = new Map();
    const controllers = new Set();
    let loginPromise = null;
    let activeToken = null;
    let rateLimitedUntil = 0;
    let disposed = false;
    let mutationPending = false;
    let uncertainRedemption = false;
    let cacheRevision = 0;

    const scope = () => {
      const config = settings();
      if (!config.subscriberId) throw new ApiError('Rewards configuration is unavailable. Please refresh the page.');
      return encodeURIComponent(config.subscriberId);
    };
    const identity = () => {
      const value = customer();
      if (!value.id || !value.customerDigest) throw new ApiError('Please log in to see your rewards.', { status: 401 });
      return value;
    };
    const cacheKey = (name, privateData) => `ee-rewards-api:${scope()}:${privateData ? identity().id : 'public'}:${locale}:${country}:${currency}:${name}`;
    const readCache = (name, privateData = false, ttl = privateData ? CUSTOMER_TTL : PUBLIC_TTL) => {
      try {
        const cached = JSON.parse(storage?.getItem(cacheKey(name, privateData)) || 'null');
        if (cached && cached.at <= now() && now() - cached.at < ttl) return cached.value;
      } catch { /* Storage is optional; private snapshots are identity-scoped. */ }
      return null;
    };
    const saveCache = (name, value, privateData = false) => {
      try { storage?.setItem(cacheKey(name, privateData), JSON.stringify({ at: now(), value })); } catch { /* Quota/private browsing. */ }
    };
    const clearCustomerCache = () => {
      cacheRevision += 1;
      try {
        const prefix = `ee-rewards-api:${scope()}:${identity().id}:`;
        for (const key of Object.keys(storage || {})) if (key.startsWith(prefix)) storage.removeItem(key);
      } catch { /* Already signed out. */ }
    };
    cacheInvalidators.add(clearCustomerCache);
    const request = async (url, { method = 'GET', body, token, signal } = {}) => {
      if (disposed) throw new DOMException('Page closed', 'AbortError');
      if (rateLimitedUntil > now()) throw new ApiError('Rewards are busy. Please wait a moment before trying again.', { status: 429, retryAt: rateLimitedUntil });
      const controller = new AbortController();
      controllers.add(controller);
      let timedOut = false;
      const abort = () => controller.abort();
      if (signal?.aborted) abort();
      else signal?.addEventListener('abort', abort, { once: true });
      const timer = window.setTimeout(() => { timedOut = true; controller.abort(); }, 12000);
      try {
        const headers = { Accept: 'application/json' };
        if (body !== undefined) headers['Content-Type'] = 'application/json';
        if (token) headers.Authorization = `Bearer ${token}`;
        const response = await fetcher(String(url), { method, headers, body: body === undefined ? undefined : JSON.stringify(body), signal: controller.signal, credentials: 'omit' });
        if (response.status === 429) {
          const retry = response.headers?.get('Retry-After');
          const seconds = Number(retry);
          const delay = retry && Number.isFinite(seconds) ? Math.max(1, seconds) * 1000 : Math.max(1000, Date.parse(retry || '') - now()) || 30000;
          rateLimitedUntil = now() + (Number.isFinite(delay) ? delay : 30000);
          throw new ApiError('Rewards are busy. Please wait a moment before trying again.', { status: 429, retryAt: rateLimitedUntil });
        }
        let payload = {};
        if (response.status !== 204) {
          try { payload = await response.json(); } catch {
            if (response.ok) throw new ApiError('We could not confirm the rewards response. Please refresh your rewards.', { uncertain: method !== 'GET' });
          }
        }
        if (response.ok && (!payload || typeof payload !== 'object')) {
          throw new ApiError('We could not confirm the rewards response. Please refresh your rewards.', { uncertain: method !== 'GET' });
        }
        if (!response.ok) {
          const description = payload.error?.description || payload.error?.message;
          const message = response.status === 401 ? 'Please log in again to see your rewards.'
            : response.status === 403 ? description || 'This reward is not currently available to you.'
              : response.status >= 500 ? NO_DATA : description || NO_DATA;
          throw new ApiError(message, { status: response.status, uncertain: method !== 'GET' && response.status >= 500 });
        }
        if (disposed || controller.signal.aborted) throw new DOMException('Page closed', 'AbortError');
        return payload;
      } catch (error) {
        if (error instanceof ApiError) throw error;
        if (signal?.aborted || disposed) throw error;
        throw new ApiError(timedOut ? 'The rewards request timed out. Please try again.' : NO_DATA, { uncertain: method !== 'GET' });
      } finally {
        window.clearTimeout(timer);
        signal?.removeEventListener('abort', abort);
        controllers.delete(controller);
      }
    };
    const login = () => {
      if (!loginPromise) {
        // The app embed supplies a Shopify-signed customer digest. This is the
        // same login contract as the onsite SDK; no merchant key is used.
        const value = identity();
        loginPromise = request(`${API}/stores/${scope()}/customer_login`, {
          method: 'POST',
          body: {
            shopifyCustomer: { subscriberId: settings().subscriberId, shopifyCustomerId: value.id, firstName: value.firstName, lastName: value.lastName, email: value.email, phone: value.phone },
            customerDigest: value.customerDigest,
            requestedScopes: ['loyalty', 'loyaltyMembership'],
            countryCode: country,
          },
        }).then((data) => {
          if (typeof data.jwt !== 'string' || !data.jwt) throw new ApiError('Please log in again to see your rewards.');
          activeToken = data.jwt;
          return data.jwt;
        }).catch((error) => { loginPromise = null; error.uncertain = false; throw error; });
      }
      return loginPromise;
    };
    const authed = async (path, options = {}) => {
      const url = path instanceof URL ? path : new URL(`${API}/loyalty/${path}`);
      if ((options.method || 'GET') === 'GET' && !(path instanceof URL)) {
        url.searchParams.set('languageCode', locale);
        if (['customer_details', 'redemption_rules', 'earning_rules'].includes(path)) url.searchParams.set('currencyCode', currency);
      }
      let token = await login();
      try { return await request(url, { ...options, token }); } catch (error) {
        // A 401 is an explicit rejection; uncertain writes are never replayed.
        if (error.status !== 401 || options.method && options.method !== 'GET') throw error;
        if (activeToken === token) { activeToken = null; loginPromise = null; }
        token = await login();
        return request(url, { ...options, token });
      }
    };
    const coalesce = (key, fn) => {
      if (!pending.has(key)) pending.set(key, Promise.resolve().then(fn).finally(() => pending.delete(key)));
      return pending.get(key);
    };
    const publicData = (name, force = false) => coalesce(`public:${name}`, async () => {
      const cached = !force && readCache(name);
      if (cached) return cached;
      const url = new URL(`${API}/stores/${scope()}/loyalty/${name}`);
      url.searchParams.set('languageCode', locale);
      url.searchParams.set('countryCode', country);
      url.searchParams.set('currencyCode', currency);
      const data = await request(url);
      saveCache(name, data);
      return data;
    });
    const member = (name) => coalesce(`member:${cacheRevision}:${name}`, () => authed(name));
    const history = async ({ nextUrl, signal } = {}) => {
      const revision = cacheRevision;
      const url = safeUrl(nextUrl || `${API}/loyalty/transactions`, '/v1/loyalty/transactions');
      if (!nextUrl) url.searchParams.set('languageCode', locale);
      const data = await coalesce(`history:${revision}:${url}`, () => authed(url, { signal }));
      if (!Array.isArray(data.transactions)) throw new ApiError('Your points history could not be loaded.');
      const entries = data.transactions.map((item) => ({
        id: item.loyaltyTransactionId,
        title: (item.notificationText || 'Points activity') + (item.status && item.status !== 'complete' ? ` (${item.status})` : ''),
        points: item.formattedAmount ? null : item.amount,
        label: item.formattedAmount || '',
        date: item.date,
      }));
      const page = { entries, nextUrl: data.nextUrl ? safeUrl(data.nextUrl, '/v1/loyalty/transactions').href : null };
      if (!nextUrl && revision === cacheRevision) saveCache('history', page, true);
      return page;
    };
    const coupons = (nextUrl) => {
      const revision = cacheRevision;
      // Isolated compatibility endpoint: used by Okendo's onsite SDK, but not
      // yet listed in its public customer API guide. Failure stays in this tab.
      const url = safeUrl(nextUrl || `${API}/loyalty/coupons`, '/v1/loyalty/coupons');
      return coalesce(`coupons:${revision}:${url}`, async () => {
        const data = await authed(url);
        if (!Array.isArray(data.coupons)) throw new ApiError('Your claimed rewards could not be loaded.');
        const page = { coupons: data.coupons, nextUrl: data.nextUrl ? safeUrl(data.nextUrl, '/v1/loyalty/coupons').href : null };
        if (!nextUrl && revision === cacheRevision) saveCache('coupons', page, true);
        return page;
      });
    };
    const mutate = async (path, method, body) => {
      if (mutationPending) throw new ApiError('Please wait for your current rewards action to finish.');
      if (path === 'redemptions' && uncertainRedemption) throw new ApiError('We could not confirm the previous redemption. Check your claimed rewards and points history before refreshing this page.');
      mutationPending = true;
      // Even an unconfirmed write may have changed the provider's balance.
      clearCustomerCache();
      try {
        const data = await authed(path, { method, body });
        if (path === 'redemptions' && data.reward?.result?.success === false) {
          throw new ApiError('The reward could not be confirmed.', { uncertain: true });
        }
        return data;
      } catch (error) {
        if (path === 'redemptions' && error.uncertain) {
          uncertainRedemption = true;
          error.message = 'We could not confirm whether points were redeemed. Check Redeemed and your points history before refreshing. Your request has not been repeated.';
        }
        throw error;
      } finally { mutationPending = false; }
    };
    return Object.freeze({
      publicData, member, history, coupons, readCache, saveCache, clearCustomerCache,
      cacheRevision: () => cacheRevision,
      reviewRequests: () => member('review_requests'),
      reviewUrl: (requestId, productId, sequenceId) => {
        const url = new URL(`${API}/stores/${scope()}/review_request/${encodeURIComponent(requestId)}/redirect`);
        url.searchParams.set('pId', String(productId).split('-').pop());
        url.searchParams.set('seId', sequenceId);
        return url.href;
      },
      redeem: (body) => mutate('redemptions', 'POST', body),
      birthday: (body) => mutate('customer_birthday', 'PUT', body),
      enrol: (version) => mutate('customer_activation', 'POST', { enrollmentChannel: 'loyalty-page', legalPoliciesVersionAccepted: version }),
      trigger: (id) => mutate(`earning_rules/${encodeURIComponent(id)}/trigger`, 'POST', {}),
      isThrottled: () => rateLimitedUntil > now(),
      isRedemptionUncertain: () => uncertainRedemption,
      destroy() { disposed = true; cacheInvalidators.delete(clearCustomerCache); controllers.forEach((controller) => controller.abort()); controllers.clear(); loginPromise = null; activeToken = null; },
    });
  }

  function profileFromData(details, spend, program) {
    const customer = details.loyaltyCustomer;
    const tiers = (program.vipProgram?.vipTiers || []).filter((tier) => tier.isActive !== false)
      .slice().sort((a, b) => Number(a.entryRequirement?.spend || 0) - Number(b.entryRequirement?.spend || 0));
    if (!customer || !Number.isFinite(customer.points?.balance)) throw new ApiError('Your member profile could not be loaded.');
    const index = tiers.findIndex((tier) => tier.vipTierId === customer.vipTierId);
    const current = tiers[index];
    if (!current) throw new ApiError('Your membership tier could not be loaded.');
    const upgrade = spend?.upgradeTier;
    const retain = spend?.promptType === 'retain' ? spend.retainTier : null;
    const next = upgrade?.nextTier?.vipTierId
      ? tiers.find((tier) => tier.vipTierId === upgrade.nextTier.vipTierId)
      : tiers[index + 1];
    const eligibleSpend = Number.isFinite(spend?.customerSpend) ? spend.customerSpend : null;
    const target = retain ? current : next;
    const threshold = target ? Number(target.entryRequirement?.spend) : null;
    const amountToGo = Number.isFinite(retain?.amount) ? retain.amount : Number.isFinite(upgrade?.amount) ? upgrade.amount
      : next && eligibleSpend !== null && Number.isFinite(threshold) ? Math.max(0, threshold - eligibleSpend) : null;
    const kept = Boolean(retain) && amountToGo === 0;
    // Draw the bar from the same amount as "to go" so the two always agree.
    const measured = Number.isFinite(amountToGo) && Number.isFinite(threshold) && threshold > 0;
    // While keeping a tier, "Spent" shows only the spend that counts toward it
    // (the tier amount less what's to go), so it matches the bar. Okendo's
    // customerSpend can include spend from before the current tier year.
    const countedSpend = retain && measured ? Math.max(0, threshold - amountToGo) : null;
    // The upgrade deadline can differ from the expiry of the current tier.
    // Use Okendo's spend prompt, the same source as its native profile widget.
    const date = retain?.dateTo || upgrade?.dateTo || (!next ? customer.dateTierExpires : null);
    const deadline = date && !Number.isNaN(Date.parse(date))
      ? new Intl.DateTimeFormat('en-AU', { day: 'numeric', month: 'long', year: 'numeric' }).format(new Date(date)) : '';
    const locked = spend?.promptType === 'locked-upgrade';
    return { currentTier: current.name, nextTier: target?.name || null, tierIndex: index, hasNextTier: Boolean(target), nextThreshold: threshold, eligibleSpend, countedSpend,
      amountToGo: locked ? null : amountToGo, deadline, deadlinePrefix: kept ? 'Until' : 'By',
      progressLabel: locked ? 'Your tier is locked' : kept ? `You've kept ${current.name}` : retain ? `Maintain ${current.name}` : null,
      progressAriaLabel: locked ? 'Your membership tier is locked'
        : kept ? `You've kept ${current.name}${deadline ? ` until ${deadline}` : ''}`
          : retain ? `Maintain ${current.name}${deadline ? ` by ${deadline}` : ''}` : null,
      points: new Intl.NumberFormat('en-AU').format(customer.points.balance),
      progress: !target ? 100
        : measured ? Math.min(100, Math.max(0, (threshold - amountToGo) / threshold * 100))
          : threshold > 0 && eligibleSpend !== null ? Math.min(100, Math.max(0, eligibleSpend / threshold * 100)) : 0 };
  }

  function variableSelection(rule, balance, requested) {
    const reward = rule.reward || {};
    const step = Number(reward.points);
    const value = Number(reward.value);
    if (!Number.isInteger(step) || step <= 0 || !Number.isFinite(value) || value <= 0 || !Number.isFinite(balance)) return null;
    const minimum = Math.ceil(Math.max(step, Number(reward.minimumPoints) || step) / step) * step;
    const cap = reward.maximumPoints == null ? Infinity : Number(reward.maximumPoints);
    if (!(cap >= 0)) return null;
    const maximum = Math.floor(Math.min(Math.max(0, balance), cap) / step) * step;
    if (maximum < minimum) return null;
    const points = Math.min(maximum, Math.max(minimum, Math.round((Number(requested) || minimum) / step) * step));
    return { points, value: Math.round(points / step * Number(reward.value) * 100) / 100, minimum, maximum, step };
  }

  window.EliteElevenLoyaltyStorefront = Object.freeze({ createClient, ApiError,
    clearPrivateCache() { cacheInvalidators.forEach((invalidate) => invalidate()); try { clearPrivateCache(window.sessionStorage); } catch {} },
    helpers: Object.freeze({ safeUrl, profileFromData, variableSelection }) });
})();

/* ---------- loyalty-product-groups ---------- */
(() => {
  'use strict';

  // Keep every native reward card in place. Colour selection changes which
  // card is visible, preserving Okendo's own controls and redemption state.
  const grids = new WeakMap();
  const normalize = (value) => value.trim().replace(/\s+/g, ' ').toLowerCase();

  function parseProductTitle(title = '') {
    const separator = title.lastIndexOf(' - ');
    if (separator < 0) return null;

    const name = title.slice(0, separator).trim();
    const colour = title.slice(separator + 3).trim();
    return name && colour
      ? { name, colour, key: normalize(name), colourKey: normalize(colour) }
      : null;
  }

  function groupProducts(products) {
    const candidates = new Map();
    products.forEach((product, index) => {
      const identity = parseProductTitle(product.title);
      if (!identity) return;
      const entries = candidates.get(identity.key) || [];
      entries.push({ ...product, ...identity, index });
      candidates.set(identity.key, entries);
    });

    // Repeated colours may be separate offers or claimed rewards. Keep them
    // separate so grouping can never hide an offer or an unspent reward code.
    return new Map(Array.from(candidates).filter(([, entries]) => (
      entries.length > 1
      && new Set(entries.map((entry) => entry.colourKey)).size === entries.length
    )));
  }

  function restoreVisibility(entry) {
    entry.item.classList.remove('is-loyalty-product-group-hidden');
    if (!entry.visibility) return;
    for (const [attribute, value] of Object.entries(entry.visibility)) {
      if (value === null) entry.item.removeAttribute(attribute);
      else entry.item.setAttribute(attribute, value);
    }
    entry.visibility = null;
  }

  function removeOptions(entry) {
    restoreVisibility(entry);
    entry.titleElement.classList.remove('ee-loyalty-product-title-source');
    entry.options?.remove();
  }

  function selectColour(state, entries, colourKey, focus = false) {
    state.selections.set(entries[0].key, colourKey);
    entries.forEach((entry) => {
      const selected = entry.colourKey === colourKey;
      if (selected) {
        restoreVisibility(entry);
      } else {
        if (!entry.visibility) {
          entry.visibility = {
            'aria-hidden': entry.item.getAttribute('aria-hidden'),
            inert: entry.item.getAttribute('inert'),
          };
        }
        entry.item.classList.add('is-loyalty-product-group-hidden');
        entry.item.setAttribute('aria-hidden', 'true');
        entry.item.setAttribute('inert', '');
      }
      entry.options.querySelectorAll('button').forEach((button) => {
        const active = button.dataset.colourKey === colourKey;
        const pressed = String(active);
        if (button.getAttribute('aria-pressed') !== pressed) {
          button.setAttribute('aria-pressed', pressed);
        }
        if (focus && selected && active) button.focus({ preventScroll: true });
      });
    });
  }

  function createOptions(state, entry, entries, swatchPattern) {
    const options = document.createElement('div');
    options.className = 'ee-loyalty-product-options';
    options.dataset.loyaltyProductOptions = '';

    // Reserve the collection-card swatch row even when there are no choices.
    const swatches = document.createElement('div');
    swatches.className = 'ee-loyalty-product-swatches';
    options.append(swatches);

    const name = document.createElement('p');
    name.className = 'ee-loyalty-product-name';
    const sourceLink = entry.titleElement.matches('a[href]')
      ? entry.titleElement
      : entry.titleElement.querySelector('a[href]');
    if (sourceLink) {
      const link = document.createElement('a');
      link.href = sourceLink.href;
      link.textContent = entry.name;
      name.append(link);
    } else {
      name.textContent = entry.name;
    }
    options.append(name);

    if (entry.colour) {
      const colour = document.createElement('p');
      colour.className = 'ee-loyalty-product-colour';
      colour.textContent = entry.colour;
      options.append(colour);
    }

    if (entries.length > 1) {
      swatches.setAttribute('role', 'group');
      swatches.setAttribute('aria-label', `Colour for ${entry.name}`);
    }
    (entries.length > 1 ? entries : []).forEach((candidate) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'ee-loyalty-product-swatch';
      button.dataset.colourKey = candidate.colourKey;
      button.title = candidate.colour;
      button.setAttribute('aria-label', candidate.colour);

      const chip = document.createElement('span');
      chip.className = 'ee-loyalty-product-swatch-chip';
      chip.setAttribute('aria-hidden', 'true');
      // Use the same Shopify Files naming convention as the collection cards.
      if (swatchPattern) {
        const image = document.createElement('img');
        const handle = candidate.colourKey.replaceAll(' ', '-').replaceAll('/', '');
        image.alt = '';
        image.src = swatchPattern.replace('colour', `swatch-${handle}`);
        image.addEventListener('error', () => image.remove(), { once: true });
        chip.append(image);
      }
      button.append(chip);
      button.addEventListener('click', (event) => {
        event.stopPropagation();
        const currentEntries = state.groups.get(entry.key);
        if (currentEntries) selectColour(state, currentEntries, candidate.colourKey, true);
      });
      swatches.append(button);
    });
    entry.titleElement.before(options);
    entry.titleElement.classList.add('ee-loyalty-product-title-source');
    return options;
  }

  function syncGrid(grid, products, swatchPattern = '') {
    let state = grids.get(grid);
    if (!state) {
      state = { entries: new Map(), orders: new Map(), selections: new Map(), groups: new Map() };
      grids.set(grid, state);
    }

    const groups = groupProducts(products);
    const groupedItems = new Set(Array.from(groups.values()).flat().map(({ item }) => item));
    const singles = products
      .filter(({ item }) => !groupedItems.has(item))
      .map((product) => ({ product, identity: parseProductTitle(product.title) || { name: product.title } }));
    const singleItems = new Set(singles.map(({ product }) => product.item));
    const currentItems = new Set(products.map(({ item }) => item));
    for (const [item, entry] of state.entries) {
      if (!groupedItems.has(item) && !singleItems.has(item)) {
        removeOptions(entry);
        state.entries.delete(item);
      }
    }
    singles.forEach(({ product, identity }) => {
      const signature = JSON.stringify(['single', product.title]);
      let entry = state.entries.get(product.item);
      if (entry && (
        entry.signature !== signature
        || entry.titleElement !== product.titleElement
        || !product.card.contains(entry.options)
      )) {
        removeOptions(entry);
        state.entries.delete(product.item);
        entry = null;
      }
      if (!entry) {
        entry = { ...product, ...identity, signature };
        state.entries.set(product.item, entry);
      }
      if (!entry.options?.isConnected) entry.options = createOptions(state, entry, [entry], swatchPattern);
      entry.titleElement.classList.add('ee-loyalty-product-title-source');
    });
    for (const [item, order] of state.orders) {
      if (!groups.size || !currentItems.has(item)) {
        if (order) item.style.setProperty('--ee-loyalty-product-order', order);
        else item.style.removeProperty('--ee-loyalty-product-order');
        state.orders.delete(item);
      }
    }
    grid.classList.toggle('has-loyalty-product-groups', groups.size > 0);
    state.groups.clear();
    if (!groups.size) return;

    const setOrder = (item, index) => {
      if (!state.orders.has(item)) {
        state.orders.set(item, item.style.getPropertyValue('--ee-loyalty-product-order'));
      }
      const order = String(index);
      if (item.style.getPropertyValue('--ee-loyalty-product-order') !== order) {
        item.style.setProperty('--ee-loyalty-product-order', order);
      }
    };
    const groupOrders = new Map();
    groups.forEach((entries) => entries.forEach(({ item }) => {
      groupOrders.set(item, entries[0].index);
    }));
    products.forEach(({ item }, index) => setOrder(item, groupOrders.get(item) ?? index));

    groups.forEach((productsInGroup, key) => {
      const signature = JSON.stringify([swatchPattern, productsInGroup.map(({ title }) => title)]);
      const entries = productsInGroup.map((product) => {
        let entry = state.entries.get(product.item);
        if (entry && (
          entry.signature !== signature
          || entry.titleElement !== product.titleElement
          || !product.card.contains(entry.options)
        )) {
          removeOptions(entry);
          state.entries.delete(product.item);
          entry = null;
        }
        if (!entry) {
          entry = { ...product, signature };
          state.entries.set(product.item, entry);
        }
        return entry;
      });
      state.groups.set(key, entries);
      entries.forEach((entry) => {
        if (!entry.options?.isConnected) {
          entry.options = createOptions(state, entry, entries, swatchPattern);
        }
        entry.titleElement.classList.add('ee-loyalty-product-title-source');
      });
      const selected = state.selections.get(key);
      selectColour(state, entries, entries.some(({ colourKey }) => colourKey === selected)
        ? selected
        : entries[0].colourKey);
    });
  }

  window.EliteElevenLoyaltyProductGroups = Object.freeze({
    syncGrid,
    helpers: Object.freeze({ parseProductTitle, groupProducts }),
  });
})();

/* ---------- loyalty-api-page ---------- */
(() => {
  'use strict';

  const number = (value) => new Intl.NumberFormat('en-AU').format(value);
  const money = (value, currency = 'AUD') => new Intl.NumberFormat('en-AU', { style: 'currency', currency, maximumFractionDigits: 2, minimumFractionDigits: Number.isInteger(value) ? 0 : 2 }).format(value);
  const rootPath = () => window.Shopify?.routes?.root || '/';
  const urlFor = (value) => {
    if (typeof value !== 'string' || !value.trim()) return '';
    try { const url = new URL(value, window.location.origin); return ['https:', 'http:'].includes(url.protocol) ? url.href : ''; } catch { return ''; }
  };
  const node = (tag, className = '', text) => {
    const element = document.createElement(tag);
    if (className) element.className = className;
    if (text !== undefined) element.textContent = text;
    return element;
  };
  const button = (text, action, className = 'ee-okendo-action') => {
    const element = node('button', className, text);
    element.type = 'button';
    if (action) element.addEventListener('click', action);
    return element;
  };
  const loadingIndicator = (label, inline = false) => {
    const wrapper = node('span', 'loyalty-loading');
    const spinner = node('span', `loyalty-spinner${inline ? ' loyalty-spinner--inline' : ''}`);
    spinner.setAttribute('aria-hidden', 'true');
    wrapper.append(spinner, node('span', 'loyalty-visually-hidden', label));
    return wrapper;
  };
  const message = (parent, text) => {
    let status = parent.querySelector('[data-api-message]');
    if (!status) {
      status = node('p', 'access-section-copy');
      status.dataset.apiMessage = '';
      status.setAttribute('role', 'status');
      parent.append(status);
    }
    status.textContent = text;
    status.hidden = !text;
    return status;
  };

  function earnText(rule, birthday) {
    const reward = rule.reward || {};
    if (reward.type === 'submit-review') {
      const maximum = ['amountForReview', 'amountForImage', 'amountForVideo', 'amountForSocialLogin'].reduce((sum, key) => sum + (Number(reward[key]) || 0), 0);
      return `Up to +${number(maximum)} Points`;
    }
    const amount = Number(reward.amount);
    if (!Number.isFinite(amount)) return rule.pointsPerText || '';
    if (reward.type === 'points-per-spend') return `+${number(amount)} Points for every $1 spent`;
    if (rule.action === 'birthday' && birthday?.day && birthday?.month) {
      const date = new Intl.DateTimeFormat('en-AU', { day: 'numeric', month: 'long', timeZone: 'UTC' }).format(new Date(Date.UTC(2000, birthday.month - 1, birthday.day)));
      return `+${number(amount)} Points on ${date}`;
    }
    return `+${number(amount)} Points`;
  }

  function couponStatus(coupon) {
    if (coupon.status !== 'issued') return coupon.status || 'unknown';
    if (coupon.expiryDate && Date.parse(coupon.expiryDate) <= Date.now()) return 'expired';
    return 'issued';
  }

  function reviewRequestsFromData(data) {
    return (data.sequenceActors || [])
      .flatMap((actor) => (actor.productsToReview || []).map((product) => ({
        ...product, reviewRequestId: actor.reviewRequestId, sequenceElementId: actor.sequenceElementId, reward: actor.reward,
      })))
      .filter((review) => review.reviewRequestId && review.productId && review.sequenceElementId);
  }

  // Most points a saved review offer can earn, or null when it isn't a points
  // offer (coupons) or can't be read. Okendo saves the offer when the request
  // is sent; order dates and the current earning rule cannot identify it.
  function reviewPoints(reward) {
    if (!reward || reward.type && reward.type !== 'points') return null;
    const keys = ['reviewRewardText', 'imageRewardText', 'videoRewardText', 'socialRewardText'];
    if (reward.reviewRewardText == null || String(reward.reviewRewardText).trim() === '') return null;
    const amounts = keys.map((key) => Number(reward[key] ?? 0));
    const limit = Number(reward.rewardLimitText ?? 0);
    if (amounts.some((amount) => !Number.isFinite(amount) || amount < 0) || !Number.isFinite(limit) || limit < 0) return null;
    const total = amounts.reduce((sum, amount) => sum + amount, 0);
    return limit > 0 ? Math.min(total, limit) : total;
  }

  // Only reviews that earn loyalty points are listed on the rewards page.
  const reviewEarnsPoints = (review) => reviewPoints(review?.reward) > 0;

  function reviewRewardText(reward) {
    if (reward?.type === 'coupon') return 'Discount offer · no loyalty points';
    const maximum = reviewPoints(reward);
    if (maximum === null) return 'See offer on review form';
    return maximum > 0 ? `Up to ${number(maximum)} points` : 'No points offered';
  }

  function mount(root, { profile, initializeHistory, convertCopy }) {
    const { createClient, helpers, ApiError } = window.EliteElevenLoyaltyStorefront;
    const client = createClient();
    const signedIn = root.dataset.apiMember === 'true';
    const loginUrl = root.dataset.apiLogin || `${rootPath()}account/login`;
    const modules = Object.fromEntries(Array.from(root.querySelectorAll('[data-api-module]')).map((element) => [element.dataset.apiModule, element]));
    const hero = root.querySelector('[data-loyalty-hero]');
    const heroHeading = hero?.getAttribute('aria-labelledby');
    const selections = new Map();
    const products = new Map();
    const cleanups = [];
    const rendered = new Map();
    const actionStates = new WeakMap();
    const trackAction = (control, disabled) => {
      control.dataset.apiLiveAction = '';
      actionStates.set(control, disabled);
      control.disabled = disabled();
    };
    const updateActions = () => root.querySelectorAll('[data-api-live-action]').forEach((control) => {
      control.disabled = actionStates.get(control)?.() ?? true;
    });
    const changed = (kind, value) => {
      const key = JSON.stringify(value);
      if (rendered.get(kind) === key) return false;
      rendered.set(kind, key);
      return true;
    };
    let customer = null;
    let program = null;
    let redemptionRules = [];
    let earningRules = [];
    let localisation = null;
    let claimed = [];
    let issuedReward = null;
    let nextCoupons = null;
    let couponsLoaded = false;
    let liveCoupons = false;
    let couponsPending = null;
    let disposed = false;
    let refreshing = null;
    let refreshRevision = -1;
    let historyCleanup = null;
    let historyObserver = null;
    let busy = false;
    let liveProfile = false;
    let liveRedemptionRules = false;
    let liveEarningRules = false;
    let hasRedemptionRules = false;
    let hasEarningRules = false;
    let redemptionError = null;
    let earningError = null;
    let confirmation = null;
    let cartCount = Number(root.dataset.apiCartCount) || 0;
    let purchaseRequest = null;
    const purchaseHref = () => urlFor(cartCount > 0 ? `${rootPath()}cart` : rootPath());
    const limitedRules = new Set();
    const isEnrolled = () => liveProfile && customer?.status === 'enrolled';
    const balance = () => Number.isFinite(customer?.points?.balance) ? customer.points.balance : null;
    const checkActive = () => { if (disposed) throw new DOMException('Page closed', 'AbortError'); };
    const sourceFor = (kind) => kind === 'profile' ? profile?.source : modules[kind]?.closest('[data-loyalty-source]');
    const setSource = (kind, state, error) => {
      if (disposed) return;
      const source = sourceFor(kind);
      if (!source) return;
      const retained = state === 'error' && error?.status !== 401 && error?.status !== 403 && kind !== 'profile' && source.querySelector('[data-api-block].is-loyalty-block-ready');
      if (retained) {
        message(source, `${error?.message || 'Live rewards are temporarily unavailable.'} Showing your previously loaded rewards.`);
        source.querySelectorAll('[data-api-live-action]').forEach((control) => { control.disabled = true; });
        return;
      }
      if (state === 'ready') message(source, '');
      source.classList.toggle('is-loyalty-source-ready', state === 'ready');
      source.classList.toggle('is-loyalty-source-pending', state === 'loading');
      source.classList.toggle('is-loyalty-source-error', state === 'error');
      source.setAttribute('aria-busy', String(state === 'loading'));
      source.querySelectorAll('[data-api-block]').forEach((block) => block.classList.toggle('is-loyalty-block-ready', state === 'ready'));
      if (error) {
        const copy = source.querySelector('.loyalty-source-status-error p');
        if (copy) copy.textContent = error.message || String(error);
      }
    };
    const icon = (name, label) => {
      const wrapper = node('div', 'ee-okendo-card-icon-wrap');
      const body = node('div', 'ee-okendo-card-icon');
      const template = root.querySelector(`template[data-api-icon="${['sack-dollar', 'gift', 'star', 'cake-candles', 'thumbs-up', 'ticket'].includes(name) ? name : 'gift'}"]`);
      if (typeof name === 'object' && urlFor(name?.url)) {
        const image = node('img'); image.src = urlFor(name.url); image.alt = label; body.append(image);
      } else if (template) body.append(template.content.cloneNode(true));
      wrapper.append(body);
      return wrapper;
    };
    const card = (product = false, coupon = false) => {
      const slide = node('div', 'ee-okendo-slide swiper-slide');
      const container = node('div', 'ee-okendo-card-container');
      const body = node('article', `ee-okendo-card${product ? '' : ' c-customisableActionCard'}${coupon ? ' c-customisableCouponCard' : ''}${product && coupon ? ' ee-okendo-product-coupon' : ''}`);
      container.append(body); slide.append(container);
      return { slide, body };
    };
    const empty = (kind, count, text) => {
      const el = modules[kind]?.querySelector('[data-api-empty]');
      if (el) { el.hidden = count > 0; el.textContent = text; }
    };
    const productData = (reward) => {
      const product = reward.product || reward.productAndVariants?.product;
      const variants = reward.variants || reward.productAndVariants?.variants || [];
      return { product, variants };
    };
    const remoteId = (id) => String(id || '').replace(/^shopify-/, '').replace(/^gid:\/\/shopify\/ProductVariant\//, '');
    const resetConfirmation = () => {
      if (confirmation?.control.isConnected) confirmation.control.textContent = 'Redeem';
      confirmation = null;
    };
    const confirmRedemption = (rule, selected, control, target) => {
      const points = selected?.points ?? rule.reward?.points;
      if (confirmation?.control === control && confirmation.points === points) {
        resetConfirmation();
        return showRedemption(rule, selected, control, target);
      }
      resetConfirmation();
      confirmation = { control, points };
      control.textContent = `Spend ${number(points)} Points?`;
    };
    const shopProduct = (value) => {
      const href = urlFor(value);
      if (!href) return Promise.resolve(null);
      const url = new URL(href);
      if (url.origin !== window.location.origin || !/^\/products\/[^/]+\/?$/.test(url.pathname)) return Promise.resolve(null);
      const key = url.pathname;
      if (!products.has(key)) products.set(key, window.fetch(`${key.replace(/\/$/, '')}.js`, { credentials: 'same-origin' })
        .then((response) => response.ok ? response.json() : null).catch(() => null));
      return products.get(key);
    };
    const imageFor = (product, title) => {
      const image = node('a', 'ee-okendo-card-image');
      if (urlFor(product?.url)) image.href = urlFor(product.url);
      image.setAttribute('aria-label', `View ${title}`);
      const src = urlFor(product?.imageUrl);
      if (src) { const img = node('img'); img.src = src; img.alt = title; img.loading = 'lazy'; image.append(img); }
      return image;
    };
    const variantsFor = async (rule, select) => {
      const bundles = rule.reward?.products?.length ? rule.reward.products : [productData(rule.reward || {})];
      const choices = await Promise.all(bundles.map(async ({ product, variants = [] }) => {
        const catalog = await shopProduct(product?.url);
        const allowed = new Set(variants.map((variant) => remoteId(variant.variantRemoteId || variant.id)));
        return (catalog?.variants || variants.map((variant) => ({ id: remoteId(variant.variantRemoteId || variant.id), title: variant.title, available: true })))
          .filter((variant) => !allowed.size || allowed.has(String(variant.id)))
          .map((variant) => ({ ...variant, title: bundles.length > 1 ? `${product?.name || 'Product'} — ${variant.title}` : variant.title }));
      }));
      if (disposed || !select.isConnected) return;
      const eligible = choices.flat();
      select.replaceChildren();
      for (const variant of eligible) {
        const option = node('option', '', variant.title + (variant.available === false ? ' — Sold out' : ''));
        option.value = String(variant.id); option.disabled = variant.available === false; select.append(option);
      }
      if (eligible.length) select.value = String(eligible.find((variant) => variant.available !== false)?.id || '');
      select.disabled = !eligible.some((variant) => variant.available !== false);
      // A single default variant needs no visible selector, but remains the cart choice.
      select.hidden = eligible.length <= 1 && (!eligible[0] || eligible[0].title === 'Default Title');
      // Show the choices as the product page's option buttons (theme markup and
      // styles); the hidden select stays the source of truth for Apply to cart.
      const firstOption = bundles.length === 1 ? (await shopProduct(bundles[0].product?.url))?.options?.[0] : null;
      const optionName = typeof firstOption === 'string' ? firstOption : firstOption?.name || '';
      if (disposed || !select.isConnected) return;
      variantButtons(select, eligible, optionName);
    };
    let variantGroupCount = 0;
    const variantButtons = (select, variants, optionName) => {
      select.nextElementSibling?.matches('.ee-loyalty-variant') && select.nextElementSibling.remove();
      if (select.hidden || !variants.length) return;
      const group = `ee-loyalty-variant-${variantGroupCount += 1}`;
      const wrapper = node('div', 'variant-wrapper ee-loyalty-variant'); wrapper.dataset.type = 'button';
      const heading = node('div', 'product-label-wrapper');
      const label = node('label', 'variant__label', `Select ${optionName || 'Option'}`); label.htmlFor = group;
      heading.append(label);
      const fieldset = node('fieldset', 'variant-input-wrap'); fieldset.id = group;
      fieldset.append(node('legend', 'hide', optionName || 'Option'));
      variants.forEach((variant, index) => {
        const id = `${group}-${index}`;
        const item = node('div', 'variant-input');
        const input = node('input'); input.type = 'radio'; input.name = group; input.id = id; input.value = String(variant.id);
        input.checked = select.value === String(variant.id);
        const soldOut = variant.available === false;
        if (soldOut) { input.disabled = true; input.classList.add('disabled'); }
        const text = node('label', `variant__button-label${soldOut ? ' disabled' : ''}`, variant.title); text.htmlFor = id;
        if (soldOut) text.title = 'Sold out';
        input.addEventListener('change', () => {
          if (!input.checked) return;
          select.value = input.value;
          select.dispatchEvent(new Event('change', { bubbles: true }));
        });
        item.append(input, text); fieldset.append(item);
      });
      wrapper.append(heading, fieldset);
      select.classList.add('ee-loyalty-variant-source');
      select.after(wrapper);
    };
    const afterMutation = async () => {
      couponsLoaded = false;
      await refresh();
      historyCleanup?.refresh?.();
      if (Object.values(modules).some((module) => module.querySelector('[data-api-tab="redeemed"][aria-selected="true"]'))) await loadCoupons();
    };
    const perform = async (control, action, target, { redemption = false } = {}) => {
      if (busy) return;
      busy = true;
      const label = control.textContent;
      control.disabled = true;
      control.setAttribute('aria-busy', 'true');
      control.replaceChildren(loadingIndicator('Updating your rewards…', true));
      message(target, '');
      try {
        const result = await action();
        checkActive();
        if (redemption && result.reward?.result?.success === false) throw new ApiError(result.reward.result.error?.message || 'We could not issue your reward. Check your points history before trying again.');
        await afterMutation();
        return result;
      } catch (error) {
        if (!disposed && error.name !== 'AbortError') message(target, error.message || 'Your rewards could not be updated. Please try again.');
      } finally {
        busy = false;
        if (!disposed) renderRewards();
        if (!disposed && control.isConnected) {
          control.textContent = label;
          control.setAttribute('aria-busy', 'false');
          control.disabled = redemption && client.isRedemptionUncertain() || client.isThrottled();
        }
        if (!disposed) updateActions();
      }
    };
    const showRedemption = async (rule, selected, control, target) => {
      if (!isEnrolled() || !liveRedemptionRules || balance() === null) return;
      const payload = { redemptionRuleId: rule.redemptionRuleId, channel: 'embedded' };
      if (selected) payload.variableValue = { points: selected.points, value: selected.value };
      const rewardValue = selected?.value ?? rule.reward?.value;
      if (localisation?.currency && Number.isFinite(Number(rewardValue)) && ['fixed-value', 'variable-value'].includes(rule.type)) {
        const currency = localisation.currency;
        payload.localisation = { currencyCode: currency.code, clientValue: Number((Number(rewardValue) * currency.exchangeRate).toFixed(2)), locale: window.Shopify?.locale || 'en' };
      }
      const result = await perform(control, () => client.redeem(payload), target, { redemption: true });
      if (!result || disposed) return;
      if (result.isLimitReached) { limitedRules.add(rule.redemptionRuleId); renderRewards(); }
      const code = result.reward?.result?.data?.code || result.reward?.code;
      const kind = rule.type === 'free-product' ? 'products' : 'redeem';
      if (code) {
        const reward = rule.reward || {};
        issuedReward = { kind, coupon: { code, description: rule.title || 'Your reward', status: 'issued', displayType: kind === 'products' ? 'free-product' : 'discount',
          ...(reward.product ? { products: [{ product: reward.product, variants: reward.variants || [] }] } : {}) } };
        renderCoupons();
      } else if (result.reward?.rewardType === 'store-credit' || result.reward?.type === 'store-credit') {
        message(modules[kind] || target, 'Your store credit has been issued.');
      }
      // Render the provider's updated coupon list; never invent a coupon status.
      modules[kind]?.querySelector('[data-api-tab="redeemed"]')?.click();
    };
    const conversionCard = (rule) => {
      const reward = rule.reward || {};
      const selected = rule.type === 'variable-value' ? helpers.variableSelection(rule, balance() ?? 0, selections.get(rule.redemptionRuleId)) : null;
      const minimum = rule.type === 'variable-value' ? helpers.variableSelection(rule, Number.MAX_SAFE_INTEGER)?.minimum : null;
      if (balance() !== null && minimum && balance() < minimum) {
        const prompt = node('div', 'access-redeemed-guest');
        prompt.append(node('p', 'access-section-copy', `You need a minimum of ${number(minimum)} points to convert your points. You currently have ${number(balance())} ${balance() === 1 ? 'point' : 'points'}.`));
        const earn = node('a', 'access-button', 'Earn points'); earn.href = '#earn'; prompt.append(earn);
        return prompt;
      }
      const { slide, body } = card();
      const titleArea = node('div', 'ee-okendo-card-title-area');
      titleArea.append(node('h3', 'ee-okendo-card-title', rule.title || 'Convert Your Points'));
      body.append(titleArea);
      const content = node('div', 'ee-okendo-card-content');
      const actions = node('div', 'ee-okendo-action-wrap');
      let current = selected;
      const redeem = button('Redeem', () => confirmRedemption(rule, current, redeem, body));
      redeem.dataset.apiRedeem = '';
      trackAction(redeem, () => !isEnrolled() || !liveRedemptionRules || balance() === null || busy || client.isRedemptionUncertain() || limitedRules.has(rule.redemptionRuleId) || rule.type === 'variable-value' && !selected || balance() < Number(reward.points || 0));
      if (selected) {
        const slider = node('div', 'c-slider--customisable');
        const range = node('input', 'ee-okendo-range');
        range.type = 'range'; range.min = '0'; range.max = String(selected.maximum); range.step = 'any'; range.value = String(selected.points);
        range.setAttribute('aria-label', 'Points to convert');
        const info = node('div', 'ee-okendo-slider-info');
        const sr = node('span', 'sr-only'); const points = node('span', 'ee-okendo-card-value'); const value = node('span');
        info.append(sr, points, value);
        const draw = () => {
          current = helpers.variableSelection(rule, balance(), range.value);
          if (!current) return;
          const formatted = money(current.value);
          points.textContent = `${number(current.points)} Points`; value.textContent = formatted;
          sr.textContent = `Redeem ${number(current.points)} points for ${formatted}`;
          range.setAttribute('aria-valuetext', sr.textContent);
          range.style.setProperty('--ee-redeem-progress', `${Number(range.value) / selected.maximum * 100}%`);
          const copy = convertCopy(String(current.points), formatted, String(balance()));
          body.style.setProperty('--ee-convert-rate', JSON.stringify(copy.rate));
          // Keep the shared stylesheet's full "of your" copy on this card only.
          body.style.setProperty('--ee-convert-line', JSON.stringify(copy.line));
          root.querySelector('#redeem')?.setAttribute('data-convert-ready', '');
          selections.set(rule.redemptionRuleId, current.points);
        };
        range.addEventListener('input', draw);
        range.addEventListener('input', resetConfirmation);
        range.addEventListener('change', () => { if (current) range.value = String(current.points); draw(); });
        range.addEventListener('keydown', (event) => {
          if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();
          range.value = String(event.key === 'Home' ? selected.minimum : event.key === 'End' ? selected.maximum : (current?.points || selected.minimum) + (['ArrowLeft', 'ArrowDown'].includes(event.key) ? -selected.step : selected.step));
          draw();
        });
        slider.append(range); content.append(slider, info); draw();
      } else {
        const value = rule.type === 'percentage-coupon' ? `${reward.value}% off` : rule.type === 'free-shipping' ? 'Free shipping' : money(Number(reward.value) || 0);
        titleArea.append(node('p', 'c-customisableActionCard-titleArea-subtitle', value));
        const minimum = Number(reward.minimumPoints || reward.points || 0);
        body.append(node('p', 'c-customisableActionCard-body', rule.type === 'variable-value'
          ? `You need ${number(minimum)} points to start converting your points.` : `${number(minimum)} points`));
      }
      body.append(content); actions.append(redeem); body.append(actions);
      return slide;
    };
    const productCard = (rule, guest = false) => {
      const { product, variants } = productData(rule.reward || {});
      const title = (rule.title || product?.name || 'Product reward').replace(/^Free\s+/i, '');
      const { slide, body } = card(true);
      body.dataset.loyaltyProductTitle = title;
      body.append(imageFor(product, title));
      const titleArea = node('div', 'ee-okendo-card-title-area');
      const heading = node('h3', 'ee-okendo-card-title', title);
      const cost = Number(rule.reward?.points || 0);
      const value = node('p', 'ee-okendo-card-value', `${number(cost)} ${cost === 1 ? 'Point' : 'Points'}`);
      titleArea.append(heading, value);
      body.append(titleArea);
      const actions = node('div', 'ee-okendo-action-wrap');
      if (guest) {
        const link = node('a', 'ee-okendo-action', 'Log in to redeem'); link.href = loginUrl; actions.append(link);
      } else {
        const redeem = button('Redeem', () => confirmRedemption(rule, null, redeem, body));
        redeem.dataset.apiRedeem = '';
        trackAction(redeem, () => !isEnrolled() || !liveRedemptionRules || balance() === null || balance() < Number(rule.reward?.points || 0) || busy || client.isRedemptionUncertain() || limitedRules.has(rule.redemptionRuleId));
        actions.append(redeem);
      }
      body.append(actions);
      return { slide, card: body, titleElement: heading, item: slide, title, variants };
    };
    const renderRewards = () => {
      if (disposed || !hasRedemptionRules) return;
      const conversions = redemptionRules.filter((rule) => rule.type !== 'free-product');
      const productRules = redemptionRules.filter((rule) => rule.type === 'free-product');
      const state = [balance(), customer?.status, [...limitedRules]];
      if (modules.redeem && signedIn) {
        if (changed('redeem', [conversions, localisation, state])) {
          resetConfirmation();
          modules.redeem.querySelector('[data-api-grid]').replaceChildren(...conversions.map(conversionCard));
        }
        empty('redeem', conversions.length, 'There are no rewards available for your membership at the moment.');
        setSource('redeem', redemptionError ? 'error' : 'ready', redemptionError);
      }
      const grid = modules.products?.querySelector('[data-api-grid]') || (!signedIn && root.querySelector('.rewards-products-public'));
      if (grid) {
        if (changed('products', [productRules, state])) {
          resetConfirmation();
          const cards = productRules.map((rule) => productCard(rule, !signedIn));
          grid.replaceChildren(...cards.map((item) => item.slide));
          window.EliteElevenLoyaltyProductGroups?.syncGrid(grid, cards, root.dataset.loyaltySwatchImage || '');
        }
        empty('products', productRules.length, 'There are no product rewards available at the moment.');
        setSource('products', redemptionError ? 'error' : 'ready', redemptionError);
      }
      updateActions();
    };
    const editBirthday = (rule, body, titleArea, actions, control) => {
      if (body.querySelector('form')) return;
      const form = node('form', 'c-birthdayForm');
      const fields = {};
      for (const [key, placeholder, min, max] of [['day', 'Day', 1, 31], ['month', 'Month', 1, 12], ...(rule.dateComponentsToCapture === 'day-month-year' ? [['year', 'Year', 1900, new Date().getFullYear()]] : [])]) {
        const input = node('input', 'ee-okendo-field'); input.type = 'number'; input.name = key; input.placeholder = placeholder;
        input.setAttribute('aria-label', placeholder); input.min = String(min); input.max = String(max); input.required = true;
        if (customer?.birthday?.[key]) input.value = String(customer.birthday[key]);
        fields[key] = input; form.append(input);
      }
      const save = button('Save Date'); save.type = 'submit';
      const cancel = button('Cancel', () => { form.remove(); actions.hidden = false; }, 'ee-okendo-action ee-okendo-cancel-action');
      actions.classList.add('access-tab-panel');
      form.append(save, cancel); actions.hidden = true; body.append(form);
      form.addEventListener('submit', async (event) => {
        event.preventDefault(); if (!form.reportValidity()) return;
        const date = Object.fromEntries(Object.entries(fields).map(([key, field]) => [key, Number(field.value)]));
        const year = date.year || 2000; const parsed = new Date(Date.UTC(year, date.month - 1, date.day));
        if (parsed.getUTCDate() !== date.day || parsed.getUTCMonth() !== date.month - 1 || date.year && parsed > new Date()) { message(form, 'Please enter a valid birthday.'); return; }
        await perform(save, () => client.birthday(date), form);
      });
      fields.day?.focus();
    };
    // Review requests open in a popup: one row per product, with the colour
    // on a second line and a small Review button (the inline stack of full-width
    // buttons was hard to scan).
    const reviewImage = (review) => {
      const value = review.imageUrl || review.productImageUrl || review.image?.url || review.image?.src || review.image;
      return typeof value === 'string' && value ? value : '';
    };
    const openReviewDialog = (rule, reviews, returnFocus) => {
      root.querySelector('.access-review-dialog')?.remove();
      const dialog = node('dialog', 'access-review-dialog');
      dialog.setAttribute('aria-labelledby', 'access-review-dialog-title');
      const panel = node('div', 'access-review-panel');
      const head = node('div', 'access-review-head');
      const heading = node('div', 'access-review-heading');
      const title = node('h2', 'access-review-title', rule?.title || 'Write a Review'); title.id = 'access-review-dialog-title';
      heading.append(title);
      heading.append(node('p', 'access-review-value', 'Products eligible for points will appear here.'));
      const close = node('button', 'access-review-close'); close.type = 'button'; close.setAttribute('aria-label', 'Close');
      close.innerHTML = '<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"><path d="M6 6l12 12M18 6L6 18"/></svg>';
      head.append(heading, close);
      const list = node('ul', 'access-review-list');
      for (const review of reviews) {
        const fullName = String(review.name || review.title || 'Your purchase');
        const split = fullName.lastIndexOf(' - ');
        const productName = split > 0 ? fullName.slice(0, split) : fullName;
        const variant = split > 0 ? fullName.slice(split + 3) : '';
        const offer = reviewRewardText(review.reward);
        const item = node('li', 'access-review-item');
        const link = node('a', 'access-review-link');
        link.href = client.reviewUrl(review.reviewRequestId, review.productId, review.sequenceElementId);
        link.target = '_blank'; link.rel = 'noopener';
        link.setAttribute('aria-label', `Review ${fullName}. ${offer}.`);
        const image = reviewImage(review);
        if (image) {
          const thumb = node('span', 'access-review-thumb');
          const img = node('img'); img.src = image; img.alt = ''; img.loading = 'lazy'; img.width = 56; img.height = 56;
          thumb.append(img); link.append(thumb);
        }
        const text = node('span', 'access-review-text');
        text.append(node('span', 'access-review-name', productName));
        if (variant) text.append(node('span', 'access-review-variant', variant));
        text.append(node('span', 'access-review-variant access-review-reward', offer));
        link.append(text, node('span', 'access-review-cta', 'Review'));
        item.append(link); list.append(item);
      }
      panel.append(head);
      if (list.children.length) panel.append(list);
      else panel.append(node('p', 'access-review-empty', 'You have nothing to review at the moment.'));
      dialog.append(panel);
      root.append(dialog);
      const finish = () => {
        document.documentElement.classList.remove('access-review-open');
        dialog.remove();
        returnFocus?.focus?.();
      };
      close.addEventListener('click', () => dialog.close());
      dialog.addEventListener('click', (event) => { if (event.target === dialog) dialog.close(); });
      dialog.addEventListener('close', finish);
      document.documentElement.classList.add('access-review-open');
      if (typeof dialog.showModal === 'function') dialog.showModal(); else dialog.setAttribute('open', '');
      close.focus();
    };
    const showReviews = async (control, body, rule) => {
      const label = control.textContent;
      control.disabled = true;
      control.setAttribute('aria-busy', 'true');
      control.replaceChildren(loadingIndicator('Loading review requests…', true));
      let reviews = null;
      try {
        const data = await client.reviewRequests(); checkActive();
        reviews = reviewRequestsFromData(data).filter(reviewEarnsPoints);
      } catch (error) { if (!disposed) message(body, error.message || 'Review requests could not be loaded.'); }
      finally { if (!disposed) { control.disabled = false; control.textContent = label; control.setAttribute('aria-busy', 'false'); } }
      if (reviews && !disposed) openReviewDialog(rule, reviews, control);
    };
    const renderEarn = () => {
      const module = modules.earn;
      if (!module || disposed || !hasEarningRules) return;
      if (!changed('earn', [earningRules, customer?.birthday, customer?.referralSender, customer?.status])) {
        setSource('earn', earningError ? 'error' : 'ready', earningError);
        updateActions();
        return;
      }
      const cards = earningRules.map((rule) => {
        const { slide, body } = card();
        body.append(icon(rule.icon, rule.title));
        const area = node('div', 'ee-okendo-card-title-area');
        area.append(node('h3', 'ee-okendo-card-title', rule.title), node('div', 'ee-okendo-card-value', earnText(rule, customer?.birthday)));
        const actions = node('div', 'ee-okendo-action-wrap');
        body.append(area, node('div', 'ee-okendo-card-content'), actions);
        if (!signedIn) return slide;
        if (rule.action === 'birthday') {
          const edit = button(customer?.birthday ? 'Edit Date' : 'Add Date', () => editBirthday(rule, body, area, actions, edit));
          trackAction(edit, () => !isEnrolled() || !liveEarningRules || busy); actions.append(edit);
        }
        if (rule.isCompleted || rule.action === 'birthday' && customer?.birthday || rule.action === 'sign-up' && customer?.status === 'enrolled') {
          const completed = button('Completed'); completed.disabled = true;
          const check = root.querySelector('template[data-api-icon="check"]');
          if (check) { const mark = node('span', 'c-button-customisableIconCircle ee-okendo-status-icon'); mark.append(check.content.cloneNode(true)); completed.append(mark); }
          actions.append(completed);
        } else if (rule.actionCategory === 'social') {
          const go = button('Go', () => {
            const href = urlFor(rule.socialPageLink || rule.actionLink);
            if (!href) return message(body, 'This link is not currently available.');
            window.open(href, '_blank', 'noopener,noreferrer');
            perform(go, () => client.trigger(rule.earningRuleId), body);
          });
          trackAction(go, () => !isEnrolled() || !liveEarningRules || busy); actions.append(go);
        } else if (rule.action === 'submit-review') {
          const go = button('Go', () => showReviews(go, body, rule)); trackAction(go, () => !isEnrolled() || !liveEarningRules || busy); actions.append(go);
        } else if (rule.action === 'purchase') {
          const go = node('a', 'ee-okendo-action', 'Go');
          go.dataset.apiPurchase = ''; go.href = purchaseHref(); actions.append(go);
        } else if (rule.action !== 'birthday') {
          const href = urlFor(rule.actionLink);
          if (href) { const go = node('a', 'ee-okendo-action', 'Go'); go.href = href; actions.append(go); }
          else if (rule.action === 'referral-invite') {
            const link = customer?.referralSender?.shareableLinkUrl;
            if (urlFor(link)) actions.append(copyControl(link, 'Copy referral link'));
          } else if (rule.action === 'sign-up' && !isEnrolled()) {
            actions.append(button('Join Program', () => root.querySelector('[data-api-enrolment] button')?.focus()));
          }
        }
        return slide;
      });
      module.querySelector('[data-api-grid]').replaceChildren(...cards);
      empty('earn', cards.length, 'There are no ways to earn available at the moment.');
      setSource('earn', earningError ? 'error' : 'ready', earningError);
      updateActions();
    };
    function copyControl(code, label = 'Copy coupon code') {
      const shell = node('div', 'ee-okendo-coupon ee-okendo-coupon-shell');
      const input = node('input', 'ee-okendo-coupon-field'); input.readOnly = true; input.value = code; input.setAttribute('aria-label', label);
      const feedback = node('span', 'ee-okendo-copy-feedback'); feedback.setAttribute('role', 'status');
      let feedbackTimer;
      const showFeedback = (text) => {
        window.clearTimeout(feedbackTimer);
        feedback.textContent = text;
        feedbackTimer = window.setTimeout(() => { feedback.textContent = ''; copy.setAttribute('aria-label', label); }, 3000);
      };
      const copy = button('', async () => {
        try { await window.navigator.clipboard.writeText(code); if (disposed) return; copy.setAttribute('aria-label', 'Copied'); showFeedback('Copied'); }
        catch { if (disposed) return; input.focus(); input.select(); showFeedback('Select and copy your code.'); }
      }, 'ee-okendo-coupon-button ee-okendo-action');
      const artwork = root.querySelector('template[data-api-icon="copy"]');
      if (artwork) { const glyph = node('span', 'ee-okendo-copy-icon'); glyph.append(artwork.content.cloneNode(true)); copy.append(glyph); }
      else copy.textContent = 'Copy';
      cleanups.push(() => window.clearTimeout(feedbackTimer));
      copy.setAttribute('aria-label', label); shell.append(input, copy, feedback); return shell;
    }
    const applyCoupon = async (coupon, select, control, body) => {
      if (!liveCoupons || !isEnrolled()) return;
      const label = control.textContent;
      control.disabled = true;
      control.setAttribute('aria-busy', 'true');
      control.replaceChildren(loadingIndicator('Applying your reward…', true));
      try {
        if (select) {
          if (!select.value || select.disabled) throw new Error('Please select an available variant.');
          const response = await window.fetch(`${rootPath()}cart/add.js`, { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: [{ id: remoteId(select.value), quantity: 1 }] }) });
          if (!response.ok) throw new Error('This product could not be added to your bag. Please check availability.');
        }
        // Apply the reward code with the Cart Ajax API (keeping any codes already
        // on the cart) and open the side cart, instead of redirecting to /cart.
        // Anything unexpected falls back to the old discount-link redirect.
        const drawer = document.getElementById('CartDrawer');
        let applied = false;
        try {
          const current = await window.fetch(`${rootPath()}cart.js`, { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' } });
          const cart = current.ok ? await current.json() : {};
          const codes = (cart.discount_codes || []).map((entry) => entry.code).filter(Boolean)
            .filter((code) => code.toLowerCase() !== coupon.code.toLowerCase());
          const update = await window.fetch(`${rootPath()}cart/update.js`, { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ discount: [...codes, coupon.code].join(',') }) });
          applied = update.ok;
        } catch { applied = false; }
        if (!applied || !drawer) {
          window.location.assign(`${rootPath()}discount/${encodeURIComponent(coupon.code)}?redirect=${encodeURIComponent(`${rootPath()}cart`)}`);
          return;
        }
        if (disposed) return;
        document.dispatchEvent(new CustomEvent('cart:build'));
        document.dispatchEvent(new CustomEvent('cart:open'));
        control.disabled = false; control.textContent = label; control.setAttribute('aria-busy', 'false');
      } catch (error) { message(body, error.message); control.disabled = false; control.textContent = label; control.setAttribute('aria-busy', 'false'); }
    };
    const couponCard = (coupon, kind) => {
      const isProduct = kind === 'products';
      const { slide, body } = card(isProduct, true);
      const status = couponStatus(coupon);
      body.classList.add(`c-customisableCouponCard--${['issued', 'used', 'expired', 'cancelled'].includes(status) ? status : 'unknown'}`);
      const matching = redemptionRules.find((rule) => rule.type === 'free-product' && (coupon.redemptionRuleId && rule.redemptionRuleId === coupon.redemptionRuleId || (coupon.appliesToProductIds || []).some((id) => remoteId(id) === remoteId(rule.reward?.product?.productId))));
      const supplied = coupon.product || coupon.reward?.product;
      const product = supplied || coupon.products?.[0]?.product || matching?.reward?.product;
      const title = (product?.name || coupon.description || 'Claimed reward').replace(/^Free\s+/i, '');
      if (isProduct) {
        body.append(imageFor(product, title));
        const identity = node('div', 'ee-loyalty-coupon-identity');
        const parts = window.EliteElevenLoyaltyProductGroups?.helpers.parseProductTitle(title);
        identity.append(node('p', 'ee-loyalty-product-name', parts?.name || title));
        if (parts) identity.append(node('p', 'ee-loyalty-product-colour', parts.colour));
        body.append(identity);
      } else {
        body.append(icon('ticket', 'Coupon Icon'));
        body.append(node('h3', 'ee-okendo-card-title', coupon.description || 'Claimed reward'));
      }
      const controls = node('div', status === 'issued' && coupon.code ? 'ee-okendo-coupon c-customisableCouponCard-input' : 'ee-okendo-coupon-status c-customisableCouponCard-status-container');
      if (status === 'issued' && coupon.code) {
        let select = null;
        if (isProduct) {
          select = node('select', 'ee-okendo-field ee-okendo-coupon-field'); select.setAttribute('aria-label', `Variant for ${title}`); select.disabled = true;
          const rule = coupon.products?.length ? { reward: { products: coupon.products } } : matching || { reward: { product, variants: coupon.variants || [] } };
          body.append(select);
          variantsFor(rule, select);
        }
        if (isProduct) {
          const apply = button('Apply to cart', () => applyCoupon(coupon, select, apply, body));
          trackAction(apply, () => !liveCoupons || !isEnrolled() || !product?.url);
          controls.append(apply);
        }
        controls.append(copyControl(coupon.code));
      } else {
        if (status !== 'used') controls.append(node('span', '', { expired: 'Expired', cancelled: 'Cancelled' }[status] || 'Status unavailable'));
        if (coupon.code) controls.append(node('span', 'c-customisableCouponCard-status', coupon.code));
        if (status === 'used') {
          const tick = node('span', 'c-customisableCouponCard-tick ee-okendo-status-icon');
          const artwork = root.querySelector('template[data-api-icon="check"]');
          if (artwork) tick.append(artwork.content.cloneNode(true));
          tick.append(node('span', 'sr-only', 'Coupon Used'));
          controls.append(tick);
        }
      }
      body.append(controls); return slide;
    };
    const couponKind = (coupon) => coupon.displayType === 'free-product' || coupon.type === 'free-product' || coupon.appliesToProductIds?.length ? 'products' : 'redeem';
    const renderCoupons = () => {
      for (const kind of ['redeem', 'products']) {
        const module = modules[kind]; if (!module) continue;
        const statusOrder = { issued: 0, used: 1, expired: 2, cancelled: 3 };
        const values = claimed.filter((coupon) => couponKind(coupon) === kind)
          .sort((a, b) => (statusOrder[couponStatus(a)] ?? 4) - (statusOrder[couponStatus(b)] ?? 4));
        if (issuedReward?.kind === kind && !values.some((coupon) => coupon.code === issuedReward.coupon.code)) values.unshift(issuedReward.coupon);
        if (changed(`coupons:${kind}`, [values.map(coupon => [coupon, couponStatus(coupon)]), kind === 'products' ? redemptionRules.filter(rule => rule.type === 'free-product') : null])) {
          module.querySelector('[data-api-coupons]').replaceChildren(...values.map((coupon) => couponCard(coupon, kind)));
        }
        module.querySelector('[data-api-coupon-status]').textContent = values.length ? '' : 'You have no claimed rewards here yet.';
        module.querySelector('[data-api-coupon-more]').hidden = !nextCoupons;
      }
      updateActions();
    };
    // Only enrolled members have claimed rewards; everyone else is invited to join.
    const notEnrolledCopy = () => customer?.status === 'blocked'
      ? 'This rewards program is not currently available to your account.'
      : 'Agree and join the program to start redeeming.';
    const showNotEnrolled = () => {
      for (const module of Object.values(modules)) {
        const status = module.querySelector('[data-api-coupon-status]');
        if (status) status.textContent = notEnrolledCopy();
      }
    };
    const loadCoupons = (append = false) => {
      if (couponsPending) return couponsPending;
      if (couponsLoaded && !append) return Promise.resolve();
      if (liveProfile && customer && customer.status !== 'enrolled') {
        claimed = []; nextCoupons = null; issuedReward = null; liveCoupons = false; renderCoupons();
        showNotEnrolled();
        return Promise.resolve();
      }
      const saved = !append && client.readCache('coupons', true);
      liveCoupons = false;
      if (Array.isArray(saved?.coupons)) {
        claimed = saved.coupons; nextCoupons = saved.nextUrl;
        renderCoupons();
      }
      updateActions();
      const revision = client.cacheRevision();
      for (const module of Object.values(modules)) {
        const status = module.querySelector('[data-api-coupon-status]');
        if (status && !saved && !claimed.length) status.replaceChildren(loadingIndicator('Loading your rewards…'));
        const more = module.querySelector('[data-api-coupon-more]');
        if (more && append) { more.disabled = true; more.replaceChildren(loadingIndicator('Loading more rewards…', true)); }
      }
      couponsPending = (async () => {
        try {
          const page = await client.coupons(append ? nextCoupons : null); checkActive();
          if (revision !== client.cacheRevision()) return;
          if (issuedReward && page.coupons.some((coupon) => coupon.code === issuedReward.coupon.code)) issuedReward = null;
          const known = new Set((append ? claimed : []).map((coupon) => coupon.code || coupon.couponId));
          claimed = (append ? claimed : []).concat(page.coupons.filter((coupon) => !known.has(coupon.code || coupon.couponId)));
          nextCoupons = page.nextUrl; liveCoupons = true; renderCoupons(); couponsLoaded = true;
        } catch (error) {
          if (disposed) return;
          liveCoupons = false; updateActions();
          if (error.status === 401 || error.status === 403) {
            client.clearCustomerCache(); claimed = []; nextCoupons = null; issuedReward = null; renderCoupons();
          }
          // Okendo answers 403 ("Forbidden - Not enrolled") before the member joins.
          if (error.status === 403) { showNotEnrolled(); return; }
          for (const module of Object.values(modules)) {
            const status = module.querySelector('[data-api-coupon-status]');
            if (status) { status.textContent = error.message || 'Your claimed rewards are temporarily unavailable.'; status.append(button('Try again', () => loadCoupons(append), 'access-link')); }
          }
        } finally {
          couponsPending = null;
          if (!disposed) for (const module of Object.values(modules)) {
            const more = module.querySelector('[data-api-coupon-more]');
            if (more) { more.disabled = false; more.textContent = 'Show more'; }
          }
        }
      })();
      return couponsPending;
    };
    const updateEnrollmentPoints = () => {
      const copy = root.querySelector('[data-api-enrolment-points]'); if (!copy) return;
      const signup = earningRules.find((rule) => rule.action === 'sign-up');
      const pending = Math.trunc((balance() || 0) + (Number(signup?.reward?.amount) || 0));
      const credit = customer?.formattedPendingStoreCredit;
      copy.textContent = pending > 0
        ? `You have ${number(pending)} ${pending === 1 ? 'Point' : 'Points'}${credit ? ` and ${credit} Store Credit` : ''} waiting`
        : credit ? `You have ${credit} waiting` : 'Your perks are waiting';
    };
    const showEnrollment = async () => {
      const container = root.querySelector('[data-api-enrolment]'); if (!container) return;
      if (hero) {
        hero.removeAttribute('aria-labelledby');
        hero.setAttribute('aria-label', 'Join Elite Eleven Access');
      }
      container.hidden = false; container.replaceChildren();
      if (customer?.status === 'blocked') { container.append(node('p', 'access-section-copy', 'This rewards program is not currently available to your account.')); return; }
      const rawName = String(customer?.firstName || root.dataset.apiFirstName || '').trim();
      const firstName = rawName ? rawName.charAt(0).toUpperCase() + rawName.slice(1) : '';
      container.append(node('h2', 'access-enrolment-title', firstName ? `Hi ${firstName}` : 'Welcome'));
      const waiting = node('p', 'access-enrolment-copy'); waiting.dataset.apiEnrolmentPoints = '';
      container.append(waiting, node('p', 'access-enrolment-copy', 'Unlock them now by joining the program.'));
      updateEnrollmentPoints();
      const policies = await client.publicData('legal_policies'); checkActive();
      const legal = policies.legalPolicies || policies;
      const version = legal.version || legal.legalPoliciesVersion;
      if (!version) { container.append(node('p', 'access-section-copy', 'Program terms could not be loaded. Please try again.')); return; }
      const join = button('Join & Agree', () => perform(join, () => client.enrol(version), container), 'access-button');
      container.append(join);
      const agreement = node('p', 'access-enrolment-agreement');
      agreement.append(document.createTextNode('By joining you agree to our '));
      // Elite Eleven's own terms and privacy policy, then Okendo's (the program runs on Okendo).
      const appendPolicies = (pair) => {
        for (const [index, [label, href]] of pair.entries()) {
          if (index) agreement.append(document.createTextNode(' and '));
          const link = node('a', 'access-link', label); link.href = href; link.target = '_blank'; link.rel = 'noopener'; agreement.append(link);
        }
      };
      appendPolicies([['Terms and Conditions', urlFor(root.dataset.apiTerms) || '/pages/loyalty-t-and-c'], ['Privacy Policy', '/policies/privacy-policy']]);
      agreement.append(document.createTextNode(', and Okendo\u2019s '));
      appendPolicies([['Terms and Conditions', 'https://okendo.io/terms-conditions/'], ['Privacy Policy', 'https://okendo.io/privacy-policy/']]);
      agreement.append(document.createTextNode('.'));
      container.append(agreement);
    };
    const renderProfile = (data) => {
      if (changed('profile', data)) profile?.render(data);
      setSource('profile', 'ready');
    };
    const showSavedProfile = () => {
      const saved = client.readCache('profile', true);
      if (!saved || typeof saved.currentTier !== 'string' || !Number.isFinite(saved.progress)) return false;
      const { member, ...data } = saved;
      const points = Number(String(data.points).replace(/,/g, ''));
      if (!Number.isFinite(points)) return false;
      const birthday = member?.birthday;
      const validBirthday = Number.isInteger(birthday?.day) && birthday.day >= 1 && birthday.day <= 31
        && Number.isInteger(birthday?.month) && birthday.month >= 1 && birthday.month <= 12;
      customer = { status: 'enrolled', points: { balance: points }, birthday: validBirthday ? birthday : undefined, referralSender: member?.referralSender };
      renderProfile(data);
      root.dataset.apiProfileState = 'cached';
      return true;
    };
    const loadProfile = async () => {
      const revision = client.cacheRevision();
      try {
        const [details, spend, vip] = await Promise.all([client.member('customer_details'), client.member('customer_spend').catch((error) => {
          if (error.status === 403) return {}; throw error;
        }), client.publicData('vip_program', true)]);
        checkActive();
        if (revision !== client.cacheRevision()) return;
        customer = details.loyaltyCustomer; program = vip;
        if (!customer || !['pending', 'enrolled', 'blocked'].includes(customer.status)) throw new ApiError('Your member profile could not be loaded.');
        if (customer.status !== 'enrolled') {
          rendered.delete('profile');
          liveProfile = true;
          profile?.setProfileState('pending'); setSource('profile', 'ready'); await showEnrollment();
        } else {
          const data = helpers.profileFromData(details, spend, vip);
          liveProfile = true;
          profile?.setProfileState('ready');
          renderProfile(data);
          if (hero && heroHeading) { hero.setAttribute('aria-labelledby', heroHeading); hero.removeAttribute('aria-label'); }
          const prompt = root.querySelector('[data-api-enrolment]'); if (prompt) prompt.hidden = true;
          client.saveCache('profile', { ...data, member: { birthday: customer.birthday, referralSender: customer.referralSender } }, true);
        }
        root.dataset.apiProfileState = customer.status;
      } catch (error) {
        if (disposed) return;
        liveProfile = false;
        if (error.status === 401 || error.status === 403) { client.clearCustomerCache(); customer = null; }
        if (error.status !== 401 && error.status !== 403 && showSavedProfile()) {
          if (profile?.source) message(profile.source, 'Showing your previously saved balance. Live rewards are temporarily unavailable.');
        } else { profile?.showError(); setSource('profile', 'error', error); }
        root.dataset.apiProfileState = 'error';
      } finally {
        if (!disposed) { renderRewards(); renderEarn(); }
      }
    };
    const applyRules = (name, data, live = false) => {
      if (name === 'redemption_rules') {
        if (!Array.isArray(data?.redemptionRules) || !data.redemptionRules.every(rule => rule && typeof rule.type === 'string')) return false;
        redemptionRules = data.redemptionRules; localisation = data.localisation;
        hasRedemptionRules = true; liveRedemptionRules = live;
        redemptionError = null;
        renderRewards();
        if (claimed.length) renderCoupons();
      } else {
        if (!Array.isArray(data?.earningRules) || !data.earningRules.every(rule => rule && typeof rule.action === 'string')) return false;
        earningRules = data.earningRules;
        hasEarningRules = true; liveEarningRules = live;
        earningError = null;
        renderEarn(); updateEnrollmentPoints();
      }
      return true;
    };
    const loadRules = () => Promise.all(['redemption_rules', 'earning_rules'].map(async (name) => {
      const revision = client.cacheRevision();
      try {
        // Render saved rules separately; every page visit still gets a live check.
        const data = await (signedIn ? client.member(name) : client.publicData(name, true));
        checkActive();
        if (signedIn && revision !== client.cacheRevision()) return;
        if (!applyRules(name, data, true)) throw new ApiError('Your rewards could not be loaded.');
        if (signedIn) client.saveCache(name, data, true);
      } catch (error) {
        if (disposed) return;
        if (signedIn && (error.status === 401 || error.status === 403)) client.clearCustomerCache();
        if (name === 'redemption_rules') {
          redemptionError = error;
          liveRedemptionRules = false; renderRewards();
          if (signedIn) setSource('redeem', 'error', error);
          setSource('products', 'error', error);
        } else {
          earningError = error;
          liveEarningRules = false; renderEarn(); setSource('earn', 'error', error);
        }
      }
    }));
    function refresh() {
      if (refreshing) return refreshRevision === client.cacheRevision() ? refreshing : refreshing.then(refresh);
      refreshRevision = client.cacheRevision();
      refreshing = (async () => {
        await Promise.all([signedIn ? loadProfile() : null, loadRules()]);
        if (disposed) return;
        if (signedIn && customer?.status !== 'enrolled') client.clearCustomerCache();
        if (!disposed) root.dataset.apiReady = 'true';
      })().finally(() => { refreshing = null; });
      return refreshing;
    }
    for (const [kind, module] of Object.entries(modules)) {
      if (!signedIn) continue;
      const tab = module.querySelector('[data-api-tab="redeemed"]');
      const more = module.querySelector('[data-api-coupon-more]');
      const onTab = () => loadCoupons(); const onMore = () => loadCoupons(true);
      tab?.addEventListener('click', onTab); more?.addEventListener('click', onMore);
      cleanups.push(() => { tab?.removeEventListener('click', onTab); more?.removeEventListener('click', onMore); });
    }
    root.querySelectorAll('[data-loyalty-source-retry]').forEach((control) => {
      control.textContent = 'Try again';
      const handler = () => {
        if (client.isThrottled()) return;
        const source = control.closest('[data-loyalty-source]');
        const kind = source?.matches('[data-loyalty-account-source]') ? 'profile' : source?.querySelector('[data-api-module]')?.dataset.apiModule;
        if (kind && !source.classList.contains('is-loyalty-source-ready')) setSource(kind, 'loading');
        refresh();
      };
      control.addEventListener('click', handler); cleanups.push(() => control.removeEventListener('click', handler));
    });
    const updatePurchaseLinks = (cart) => {
      if (!Number.isInteger(cart?.item_count) || cart.item_count < 0) return;
      cartCount = cart.item_count;
      root.querySelectorAll('[data-api-purchase]').forEach(link => { link.href = purchaseHref(); });
    };
    const cartUpdated = (event) => updatePurchaseLinks(event.detail?.cart);
    const openPurchase = async (event) => {
      const link = event.target.closest?.('[data-api-purchase]');
      if (!link || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      if (purchaseRequest) return;
      const controller = new AbortController(); purchaseRequest = controller;
      const label = link.textContent;
      link.setAttribute('aria-busy', 'true');
      link.replaceChildren(loadingIndicator('Checking your cart…', true));
      const timer = window.setTimeout(() => controller.abort(), 5000);
      try {
        const response = await window.fetch(`${rootPath()}cart.js`, { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' }, signal: controller.signal });
        if (response.ok && !disposed) updatePurchaseLinks(await response.json());
      } catch { /* Keep the last known cart destination if the check fails. */ }
      finally {
        window.clearTimeout(timer); purchaseRequest = null;
        link.removeAttribute('aria-busy'); link.textContent = label;
      }
      if (!disposed) window.location.assign(purchaseHref());
    };
    document.addEventListener('cart:updated', cartUpdated);
    root.addEventListener('click', openPurchase);
    cleanups.push(() => {
      purchaseRequest?.abort();
      document.removeEventListener('cart:updated', cartUpdated);
      root.removeEventListener('click', openPurchase);
    });

    // Saved content is visible before the first network response. It is display
    // data only: liveProfile/live*Rules keep member actions disabled until checked.
    if (signedIn) showSavedProfile();
    else window.EliteElevenLoyaltyStorefront.clearPrivateCache();
    for (const name of ['redemption_rules', 'earning_rules']) applyRules(name, client.readCache(name, signedIn));

    // History stays lazy, but its saved first page appears as soon as approached.
    const startHistory = () => {
      if (historyCleanup || disposed || !signedIn) return;
      historyCleanup = initializeHistory({ readCache: () => client.readCache('history', true), loadPage: async (options) => {
        try {
          const page = await client.history(options);
          const history = root.querySelector('[data-loyalty-history]');
          if (history) message(history, '');
          return page;
        } catch (error) {
          if (error.status === 401 || error.status === 403) client.clearCustomerCache();
          throw error;
        }
      } });
    };
    const history = root.querySelector('[data-loyalty-history]');
    if (signedIn && history) {
      if ('IntersectionObserver' in window) {
        historyObserver = new IntersectionObserver((entries) => { if (entries.some((entry) => entry.isIntersecting)) { historyObserver.disconnect(); startHistory(); } }, { rootMargin: '400px' });
        historyObserver.observe(history.closest('section') || history);
      } else startHistory();
    }
    const outsideConfirmation = (event) => {
      if (confirmation && !confirmation.control.contains(event.target)) resetConfirmation();
    };
    root.addEventListener('click', outsideConfirmation);
    cleanups.push(() => root.removeEventListener('click', outsideConfirmation));
    refresh();
    return () => {
      disposed = true; client.destroy(); cleanups.forEach((cleanup) => cleanup()); historyObserver?.disconnect(); historyCleanup?.();
    };
  }

  window.EliteElevenLoyaltyApiPage = Object.freeze({ mount, helpers: Object.freeze({ earnText, couponStatus, reviewRequestsFromData, reviewRewardText, reviewEarnsPoints }) });
})();

/* ---------- loyalty-page ---------- */
(() => {
  'use strict';

  /*
   * Loyalty page controller
   * -----------------------
   * Page interactions and rendering live here. Okendo's internal selectors
   * are deliberately isolated in loyalty-okendo-adapter.js.
   */

  const instances = new WeakMap();
  const SOURCE_LOADING_TIMEOUT_MS = 10000;
  // Tier names in display order. The website now uses the first list; Okendo
  // may still report the original names, which map to the same positions.
  const TIER_NAME_SCHEMES = Object.freeze([
    Object.freeze(['community', 'inner circle', 'vip', 'elite']),
    Object.freeze(['member', 'community', 'close circle', 'family']),
  ]);

  const normalizeTier = (value) => String(value || '').trim().replace(/\s+/g, ' ').toLowerCase();

  function loadingIndicator(label, inline = false) {
    const wrapper = document.createElement('span'); wrapper.className = 'loyalty-loading';
    const spinner = document.createElement('span'); spinner.className = `loyalty-spinner${inline ? ' loyalty-spinner--inline' : ''}`;
    spinner.setAttribute('aria-hidden', 'true');
    const copy = document.createElement('span'); copy.className = 'loyalty-visually-hidden'; copy.textContent = label;
    wrapper.append(spinner, copy);
    return wrapper;
  }

  // "Community" is the first tier in the new names but the second in the
  // original ones, so the next tier name decides. Returns null when unsure.
  function resolveTierIndex(currentTier, nextTier = '') {
    const current = normalizeTier(currentTier);
    const next = normalizeTier(nextTier);
    if (!current) return null;

    let indexes = TIER_NAME_SCHEMES
      .map((names) => ({ names, index: names.indexOf(current) }))
      .filter(({ index }) => index >= 0);
    if (next) {
      const confirmed = indexes.filter(({ names, index }) => names[index + 1] === next);
      if (confirmed.length > 0) indexes = confirmed;
    }

    const unique = new Set(indexes.map(({ index }) => index));
    return unique.size === 1 ? [...unique][0] : null;
  }

  function formatCurrency(value) {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
      maximumFractionDigits: 2,
    }).format(value);
  }

  // Website glows follow the ice blue, red, cool grey and monochrome cards.
  // An unknown tier (still loading, not joined) uses Elite's neutral glow.
  const TIER_GLOWS = Object.freeze([
    Object.freeze({ rgb: '119,159,196', strength: 0.17 }),
    Object.freeze({ rgb: '169,93,104', strength: 0.22 }),
    Object.freeze({ rgb: '172,178,186', strength: 0.2 }),
    Object.freeze({ rgb: '142,146,153', strength: 0.18 }),
  ]);
  const DEFAULT_GLOW = TIER_GLOWS[TIER_GLOWS.length - 1];

  function tierGlow(index) {
    return Number.isInteger(index) && index >= 0
      ? TIER_GLOWS[Math.min(index, TIER_GLOWS.length - 1)]
      : DEFAULT_GLOW;
  }

  // Eligible spend so far: the next tier's threshold less what's still to go.
  function spentFromProfile(profile) {
    if (!profile?.hasNextTier || profile.amountToGo === null || profile.nextThreshold === null) return null;
    const threshold = Number(profile.nextThreshold);
    const toGo = Number(profile.amountToGo);
    if (!Number.isFinite(threshold) || !Number.isFinite(toGo)) return null;
    return Math.max(threshold - toGo, 0);
  }

  // "1,319 points" → "1,319" for the large points figure.
  function pointsNumber(points) {
    return String(points || '').replace(/\s*points?\s*$/i, '').trim();
  }

  function createProfileRenderer(root, { onTier, onCard } = {}) {
    const hero = root.querySelector('[data-loyalty-hero]');
    const shell = root.querySelector('.rewards-account-okendo');
    const summary = root.querySelector('[data-loyalty-summary]');
    const source = root.querySelector('[data-loyalty-account-source]');
    if (!hero || !shell || !summary || !source) return null;
    const wrap = shell.closest('[data-loyalty-profile-wrap]');

    const view = {
      tier: summary.querySelector('[data-loyalty-current-tier]'),
      points: summary.querySelector('[data-loyalty-points]'),
      spent: summary.querySelector('[data-loyalty-spent]'),
      spentStat: summary.querySelector('[data-loyalty-spent-stat]'),
      progressLabel: summary.querySelector('[data-loyalty-progress-label]'),
      amount: summary.querySelector('[data-loyalty-amount-to-go]'),
      deadline: summary.querySelector('[data-loyalty-progress-deadline]'),
      progressTrack: summary.querySelector('[data-loyalty-progress-track]'),
      progressFill: summary.querySelector('[data-loyalty-progress-fill]'),
      card: hero.querySelector('[data-loyalty-tier-card]'),
      cardImage: hero.querySelector('[data-loyalty-tier-card-image]'),
    };

    let renderVersion = 0;

    const setCard = (tierIndex, label) => {
      const slot = Number.isInteger(tierIndex) && tierIndex >= 0 ? Math.min(tierIndex, 3) + 1 : 1;
      const glow = tierGlow(tierIndex);
      hero.style.setProperty('--access-glow', glow.rgb);
      hero.style.setProperty('--access-glow-strength', String(glow.strength));
      if (!view.card || !view.cardImage) return;
      const ink = view.card.getAttribute(`data-ink-${slot}`);
      if (ink) view.card.style.setProperty('--access-ink', ink);
      const cardSource = view.card.getAttribute(`data-card-${slot}`) || view.card.getAttribute('data-card-1');
      if (label) view.cardImage.alt = `${label} membership card`;
      if (cardSource && view.cardImage.getAttribute('src') !== cardSource) {
        hero.classList.add('is-tier-pending');
        view.cardImage.setAttribute('src', cardSource);
      }
      const video = view.card.querySelector('[data-loyalty-card-video-member]');
      const source = video?.querySelector('source');
      const videoSource = view.card.getAttribute(`data-video-${slot}`) || '';
      if (video && source) {
        video.poster = cardSource || '';
        if (source.getAttribute('data-src') !== videoSource) {
          video.pause();
          video.classList.remove('is-playing');
          source.setAttribute('data-src', videoSource);
          // Clear the old tier immediately; the video controller attaches the
          // replacement URL only when this card is visible and motion is allowed.
          source.removeAttribute('src');
          video.load();
        }
      }
      onCard?.(slot);
    };

    // Show the card once its image is ready, so it never flashes another tier.
    const revealCard = (version) => {
      const done = () => {
        if (version !== renderVersion) return;
        hero.classList.remove('is-tier-pending');
      };
      if (!view.cardImage || view.cardImage.complete) {
        done();
        return;
      }
      view.cardImage.addEventListener('load', done, { once: true });
      view.cardImage.addEventListener('error', done, { once: true });
    };

    const render = (profile) => {
      renderVersion += 1;
      const version = renderVersion;

      view.tier.textContent = profile.currentTier;
      view.points.textContent = pointsNumber(profile.points) || profile.points;
      root.dataset.memberPoints = pointsNumber(profile.points) || '';

      const tierIndex = Number.isInteger(profile.tierIndex)
        ? profile.tierIndex
        : resolveTierIndex(profile.currentTier, profile.hasNextTier ? profile.nextTier : '');
      const showEliteThreshold = !profile.hasNextTier && tierIndex === 3;
      const counted = Number.isFinite(profile.countedSpend) ? profile.countedSpend : null;
      const spent = counted !== null ? counted : Number.isFinite(profile.eligibleSpend) ? profile.eligibleSpend : spentFromProfile(profile);
      if (view.spent && view.spentStat) {
        // Elite has no next-tier amount to subtract; use the approved
        // qualifying band for its spend display.
        view.spent.textContent = counted !== null || Number.isFinite(profile.eligibleSpend)
          ? formatCurrency(spent)
          : showEliteThreshold ? '$2,000+' : spent > 0 ? formatCurrency(spent) : '—';
        view.spentStat.hidden = false;
      }

      view.progressLabel.textContent = profile.progressLabel || (profile.hasNextTier
        ? `Progress to ${profile.nextTier}`
        : "You're in our top tier");
      view.amount.textContent = profile.hasNextTier && profile.amountToGo > 0
        ? `${formatCurrency(profile.amountToGo)} to go`
        : '';

      const deadline = profile.deadline || '';
      if (view.deadline) {
        view.deadline.textContent = deadline ? `${profile.deadlinePrefix || 'By'} ${deadline}` : '';
        view.deadline.hidden = !deadline;
      }

      const progress = Math.min(Math.max(profile.progress, 0), 100);
      view.progressFill.style.width = `${progress}%`;
      view.progressTrack.setAttribute('aria-valuenow', String(Math.round(progress)));
      view.progressTrack.setAttribute(
        'aria-label',
        profile.progressAriaLabel || (profile.hasNextTier
          ? `Progress to ${profile.nextTier}${deadline ? ` by ${deadline}` : ''}`
          : 'Highest loyalty tier reached'),
      );

      onTier?.(tierIndex);
      setCard(tierIndex, profile.currentTier);

      summary.classList.add('is-loyalty-data-ready');
      summary.setAttribute('aria-busy', 'false');
      hero.classList.add('is-summary-ready');
      hero.classList.remove('is-pending-member');
      revealCard(version);
    };

    const setProfileState = (state) => {
      const isPending = state === 'pending';
      shell.classList.toggle('is-pending-member', isPending);
      hero.classList.toggle('is-pending-member', isPending);
      wrap?.classList.toggle('is-pending-member', isPending);

      if (state === 'loading') {
        renderVersion += 1;
        hero.classList.remove('is-summary-ready');
        hero.classList.add('is-tier-pending');
        summary.setAttribute('aria-busy', 'true');
      }

      if (isPending) {
        // Not joined yet: show the default card and Okendo's Join & Agree below.
        renderVersion += 1;
        setCard(null, '');
        summary.setAttribute('aria-busy', 'false');
        revealCard(renderVersion);
      }
    };

    // Unreadable data stays hidden; the source shows a branded retry message.
    const showError = () => {
      hero.classList.remove('is-summary-ready');
      hero.classList.add('is-tier-pending');
      summary.setAttribute('aria-busy', 'false');
      renderVersion += 1;
    };

    return Object.freeze({ render, setProfileState, showError, source });
  }

  function createTierHighlighter(root) {
    const grid = root.querySelector('[data-loyalty-tiers-grid]');
    const tiers = grid ? Array.from(grid.querySelectorAll(':scope > .tier')) : [];
    const link = root.querySelector('[data-loyalty-benefits-link]');
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    let currentIndex = null;

    const highlight = (index) => {
      currentIndex = Number.isInteger(index) && tiers[index] ? index : null;
      tiers.forEach((tier, tierIndex) => {
        const isCurrent = tierIndex === currentIndex;
        tier.classList.toggle('is-current-tier', isCurrent);
        let chip = tier.querySelector('[data-loyalty-tier-chip]');
        if (isCurrent && !chip) {
          chip = document.createElement('span');
          chip.className = 'tier-current-chip';
          chip.dataset.loyaltyTierChip = '';
          chip.textContent = 'Your tier';
          const name = tier.querySelector('.tier-name');
          if (name) name.after(chip);
          else tier.prepend(chip);
        } else if (!isCurrent) {
          chip?.remove();
        }
      });
    };

    // On mobile the tiers are a swipeable row, so bring the member's own tier
    // into view once the page has jumped down to the section.
    const scrollToCurrent = () => {
      if (currentIndex === null || !grid || grid.scrollWidth <= grid.clientWidth) return;
      const tier = tiers[currentIndex];
      const left = tier.getBoundingClientRect().left
        - grid.getBoundingClientRect().left
        + grid.scrollLeft
        - (parseFloat(getComputedStyle(grid).paddingInlineStart) || 0);
      grid.scrollTo({ left, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
    };

    const handleLinkClick = () => window.requestAnimationFrame(scrollToCurrent);
    link?.addEventListener('click', handleLinkClick);

    return Object.freeze({
      highlight,
      destroy() {
        link?.removeEventListener('click', handleLinkClick);
        highlight(null);
      },
    });
  }

  const APP_HANDOFF_WAIT_MS = 3000;
  const APP_HANDOFF_BRIDGE_WAIT_MS = 10000;
  const APP_HANDOFF_POLL_MS = 150;
  const APP_HANDOFF_STAY_KEY = 'ee-loyalty-stay-on-web';
  const APP_HANDOFF_AUTO_KEY = 'ee-loyalty-app-handoff-at';
  const APP_HANDOFF_SDK = 'https://cdn.tapcart.com/webbridge-sdk/webbridge.umd.js';

  // Use Tapcart-specific signals, not generic Instagram/Facebook web views.
  function isTapcartApp(win = window) {
    return /Tapcart/i.test(win.navigator?.userAgent || '')
      || win.Tapcart?.isInitialized === true
      || Boolean(win.webkit?.messageHandlers?.Tapcart);
  }

  function tapcartRewardsDestination(root) {
    const screenId = (root.dataset.tapcartLoyaltyScreenId || '').trim();
    return screenId ? { type: 'screen', id: screenId } : { type: 'internal', url: '/loyalty' };
  }

  // Storefront WebViews need their own bridge, including when the visitor has
  // chosen to stay on the website instead of taking the automatic handoff.
  function ensureTapcartBridge() {
    if (window.Tapcart || document.querySelector(`script[src="${APP_HANDOFF_SDK}"]`)) return;
    const script = document.createElement('script');
    script.src = APP_HANDOFF_SDK;
    script.async = true;
    document.head.append(script);
  }

  function readSession(key) {
    try { return window.sessionStorage.getItem(key); } catch { return null; }
  }

  function writeSession(key, value) {
    try { window.sessionStorage.setItem(key, value); } catch { /* storage unavailable */ }
  }

  function initializeAppHandoff(root) {
    const handoff = root.querySelector('[data-loyalty-app-handoff]');
    if (!handoff || window.Shopify?.designMode) return () => {};

    const params = new URLSearchParams(window.location.search);
    if (params.get('web') === '1') writeSession(APP_HANDOFF_STAY_KEY, '1');
    if (readSession(APP_HANDOFF_STAY_KEY) === '1') return () => {};

    const destination = tapcartRewardsDestination(root);
    const openLink = handoff.querySelector('[data-loyalty-app-handoff-open]');
    const stayLink = handoff.querySelector('[data-loyalty-app-handoff-stay]');
    let pollTimer = null;
    let fallbackTimer = null;
    let detectedAt = null;
    let dispatched = false;
    let stopped = false;

    const showFallback = () => {
      if (stopped || !handoff.hidden) return;
      handoff.hidden = false;
      document.documentElement.classList.add('is-loyalty-app-handoff');
      openLink?.focus({ preventScroll: true });
    };
    const waitForNavigation = () => {
      window.clearTimeout(fallbackTimer);
      fallbackTimer = window.setTimeout(showFallback, APP_HANDOFF_WAIT_MS);
    };

    const openNatively = () => {
      if (!isTapcartApp() || window.Tapcart?.isInitialized !== true || typeof window.Tapcart?.action !== 'function') return false;
      try {
        window.Tapcart.action('screen/open', { destination }, { onError: showFallback });
        return true;
      } catch {
        return false;
      }
    };

    const startedAt = Date.now();
    const check = () => {
      window.clearTimeout(pollTimer);
      if (stopped || dispatched) return;
      const now = Date.now();
      if (isTapcartApp()) {
        if (detectedAt === null) {
          detectedAt = now;
          const lastAuto = Number(readSession(APP_HANDOFF_AUTO_KEY));
          // Returning to this WebView should leave the manual choice available.
          if (lastAuto && now - lastAuto < 60000) {
            dispatched = true;
            showFallback();
            return;
          }
          waitForNavigation();
          ensureTapcartBridge();
        }
        if (openNatively()) {
          dispatched = true;
          // An unavailable bridge must not consume the one automatic attempt.
          writeSession(APP_HANDOFF_AUTO_KEY, String(now));
          waitForNavigation();
          return;
        }
        if (now - detectedAt >= APP_HANDOFF_BRIDGE_WAIT_MS) {
          showFallback();
          return;
        }
      } else if (now - startedAt >= APP_HANDOFF_WAIT_MS) {
        return;
      }
      pollTimer = window.setTimeout(check, APP_HANDOFF_POLL_MS);
    };

    const handleOpen = (event) => {
      if (openNatively()) {
        event.preventDefault();
        dispatched = true;
        window.clearTimeout(pollTimer);
        writeSession(APP_HANDOFF_AUTO_KEY, String(Date.now()));
      }
    };
    const stop = () => {
      stopped = true;
      window.clearTimeout(pollTimer);
      window.clearTimeout(fallbackTimer);
      window.removeEventListener('webbridge-loaded', check);
      document.removeEventListener('webbridge-loaded', check);
    };
    const handleStay = (event) => {
      event.preventDefault();
      writeSession(APP_HANDOFF_STAY_KEY, '1');
      stop();
      handoff.hidden = true;
      document.documentElement.classList.remove('is-loyalty-app-handoff');
    };

    openLink?.addEventListener('click', handleOpen);
    stayLink?.addEventListener('click', handleStay);
    window.addEventListener('webbridge-loaded', check);
    document.addEventListener('webbridge-loaded', check);
    check();

    return () => {
      stop();
      openLink?.removeEventListener('click', handleOpen);
      stayLink?.removeEventListener('click', handleStay);
      document.documentElement.classList.remove('is-loyalty-app-handoff');
    };
  }

  function initializeLogout(root) {
    const logout = root.querySelector('[data-loyalty-logout]');
    if (!logout) return () => {};

    let signingOut = false;
    const returnUrl = `${window.location.pathname}${window.location.search}`;

    const handleLogout = async (event) => {
      if (
        event.button !== 0
        || event.metaKey
        || event.ctrlKey
        || event.shiftKey
        || event.altKey
      ) {
        return;
      }

      event.preventDefault();
      if (signingOut) return;

      signingOut = true;
      logout.setAttribute('aria-disabled', 'true');
      logout.setAttribute('aria-busy', 'true');
      logout.replaceChildren(loadingIndicator('Signing out…', true));
      window.EliteElevenLoyaltyStorefront?.clearPrivateCache();

      try {
        const response = await window.fetch(logout.href, {
          cache: 'no-store',
          credentials: 'same-origin',
          redirect: 'manual',
        });

        if (!response.ok && response.status !== 0) {
          throw new Error(`Logout failed with status ${response.status}`);
        }

        window.location.replace(returnUrl);
      } catch {
        window.location.assign(logout.href);
      }
    };

    logout.addEventListener('click', handleLogout);
    return () => logout.removeEventListener('click', handleLogout);
  }

  // Sample members for the test login (unpublished themes only), one per tier,
  // in the same shape loyalty-okendo-adapter.js reads from Okendo.
  const TEST_PROFILES = Object.freeze([
    Object.freeze({ currentTier: 'Community', nextTier: 'Inner Circle', nextThreshold: 350, amountToGo: 149, hasNextTier: true, deadline: '15 August 2027', points: '1,319 points', progress: 57 }),
    Object.freeze({ currentTier: 'Inner Circle', nextTier: 'VIP', nextThreshold: 1000, amountToGo: 380, hasNextTier: true, deadline: '15 August 2027', points: '2,450 points', progress: 62 }),
    Object.freeze({ currentTier: 'VIP', nextTier: 'Elite', nextThreshold: 2000, amountToGo: 779, hasNextTier: true, deadline: '15 August 2027', points: '2,839 points', progress: 61 }),
    Object.freeze({ currentTier: 'Elite', nextTier: '', nextThreshold: null, amountToGo: null, hasNextTier: false, deadline: '', points: '6,120 points', progress: 100 }),
  ]);

  // Sample points history for the test login, in the history service's shape.
  const TEST_HISTORY = Object.freeze([
    { title: 'Order #1042', points: 86, completedAt: '2026-10-12T10:00:00Z' },
    { title: '$10 off reward', points: -500, completedAt: '2026-10-02T10:00:00Z' },
    { title: 'Follow on Instagram', points: 10, completedAt: '2026-09-28T10:00:00Z' },
    { title: 'Order #1011', points: 142, completedAt: '2026-09-14T10:00:00Z' },
    { title: 'Write a review', points: 50, completedAt: '2026-09-03T10:00:00Z' },
    { title: 'Order #987', points: 64, completedAt: '2026-08-21T10:00:00Z' },
    { title: 'Birthday bonus', points: 500, completedAt: '2026-08-09T10:00:00Z' },
    { title: '$25 off reward', points: -1250, completedAt: '2026-07-30T10:00:00Z' },
    { title: 'Order #950', points: 40, completedAt: '2026-07-18T10:00:00Z' },
    { title: 'Join Program', points: 50, completedAt: '2026-07-02T10:00:00Z' },
  ]);

  function testProfile(tier) {
    const index = Math.min(Math.max(Math.round(Number(tier)) || 1, 1), TEST_PROFILES.length) - 1;
    return { ...TEST_PROFILES[index] };
  }

  // Sample data requires an explicit ?test-login=1..4 preview URL. Normal
  // sign-in links always use Shopify, even on development themes. The markup
  // only exists on preview themes (see loyalty-page.liquid).
  function initializeTestLogin(root, profile) {
    const member = root.querySelector('[data-loyalty-test-member]');
    if (!member) return () => {};

    // Discard the old automatic preview login so it cannot mask real sign-in.
    try { window.sessionStorage.removeItem('ee-loyalty-test-login'); } catch {}
    const tier = new URLSearchParams(window.location.search).get('test-login');
    if (!/^[1-4]$/.test(tier || '')) return () => {};

    let bar = null;
    const names = TEST_PROFILES.map((item) => item.currentTier);

    const renderBar = (tier) => {
      if (!bar) {
        bar = document.createElement('div');
        bar.className = 'access-test-bar';
        bar.setAttribute('role', 'group');
        bar.setAttribute('aria-label', 'Test login');
        const label = document.createElement('span');
        label.className = 'access-test-bar-label';
        label.textContent = 'Test login · sample data';
        const tabs = document.createElement('div');
        tabs.className = 'access-tabs';
        names.forEach((name, index) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'access-tab';
          button.textContent = name;
          button.dataset.tier = String(index + 1);
          button.addEventListener('click', () => show(index + 1));
          tabs.append(button);
        });
        bar.append(label, tabs);
        root.append(bar);
      }
      bar.querySelectorAll('button').forEach((button) => {
        button.setAttribute('aria-pressed', String(button.dataset.tier === String(tier)));
      });
    };

    const show = (tier) => {
      const level = Math.min(Math.max(Number(tier) || 1, 1), TEST_PROFILES.length);
      member.hidden = false;
      root.classList.add('is-test-login');
      root.querySelectorAll('[data-loyalty-guest-only]').forEach((element) => { element.hidden = true; });
      root.querySelector('.access-hero--guest')?.removeAttribute('id');
      root.querySelectorAll('[data-loyalty-member-only]').forEach((element) => { element.hidden = false; });
      member.id = 'rewards-account';
      root.querySelectorAll('[data-loyalty-member-label]').forEach((element) => {
        element.textContent = element.dataset.loyaltyMemberLabel;
      });
      root.querySelectorAll('[data-loyalty-member-href]').forEach((element) => {
        element.setAttribute('href', element.dataset.loyaltyMemberHref);
      });
      profile?.render(testProfile(level));
      renderBar(level);
    };

    const handleLogout = (event) => {
      event.preventDefault();
      window.location.assign(window.location.pathname);
    };

    const logouts = Array.from(root.querySelectorAll('[data-loyalty-test-logout]'));
    logouts.forEach((link) => link.addEventListener('click', handleLogout));

    show(tier);

    return () => {
      logouts.forEach((link) => link.removeEventListener('click', handleLogout));
      bar?.remove();
    };
  }

  function initializeSourceStates(root, onProfileError) {
    const cleanups = [];
    root.querySelectorAll('[data-loyalty-source]').forEach((source) => {
      let timer = null;
      const retry = source.querySelector('[data-loyalty-source-retry]');
      const refresh = () => window.location.reload();
      const clearTimer = () => {
        if (timer !== null) window.clearTimeout(timer);
        timer = null;
      };
      const sync = () => {
        const ready = source.classList.contains('is-loyalty-source-ready');
        const failed = source.classList.contains('is-loyalty-source-error');
        source.setAttribute('aria-busy', String(!ready && !failed));
        if (ready || failed) {
          clearTimer();
          return;
        }
        if (timer !== null) return;
        timer = window.setTimeout(function giveUp() {
          timer = null;
          if (source.classList.contains('is-loyalty-source-ready')) return;
          // A background tab doesn't draw, so Okendo's widgets (and our
          // styling of them) wait until it's on screen. Keep waiting rather
          // than show the retry message to someone who hasn't looked yet.
          if (document.hidden) {
            timer = window.setTimeout(giveUp, SOURCE_LOADING_TIMEOUT_MS);
            return;
          }
          source.classList.remove('is-loyalty-source-pending');
          source.classList.add('is-loyalty-source-error');
          source.setAttribute('aria-busy', 'false');
          if (source.matches('[data-loyalty-account-source]')) onProfileError?.();
        }, SOURCE_LOADING_TIMEOUT_MS);
      };
      const observer = new MutationObserver(sync);
      observer.observe(source, { attributes: true, attributeFilter: ['class'] });
      retry?.addEventListener('click', refresh);
      sync();
      cleanups.push(() => {
        clearTimer();
        observer.disconnect();
        retry?.removeEventListener('click', refresh);
      });
    });
    return () => cleanups.forEach((cleanup) => cleanup());
  }


  // One wallet button per phone: Google on Android, Apple on iPhone/iPad.
  // Computers show a QR that opens Wallet setup on the phone.
  function walletPlatform(userAgent = '', touchPoints = 0) {
    const ua = String(userAgent);
    if (/android/i.test(ua)) return 'google';
    if (/iphone|ipad|ipod/i.test(ua) || (/macintosh/i.test(ua) && Number(touchPoints) > 1)) return 'apple';
    return 'both';
  }

  const HISTORY_PAGE = 5;
  const MONTH_LABEL = new Intl.DateTimeFormat('en-AU', { month: 'long', year: 'numeric' });
  const DAY_LABEL = new Intl.DateTimeFormat('en-AU', { day: 'numeric', month: 'short' });
  const finiteValue = (value) => value !== null && value !== undefined && value !== '' && Number.isFinite(Number(value));

  // Newest first, grouped by month. Entries without a date keep their order at the end.
  function historyRows(entries) {
    return (Array.isArray(entries) ? entries : [])
      .filter((entry) => entry && (entry.title || finiteValue(entry.points)))
      .map((entry, order) => ({ entry, order, time: Date.parse(entry.completedAt || entry.createdAt || entry.date || '') }))
      .sort((a, b) => (Number.isFinite(b.time) ? b.time : -Infinity) - (Number.isFinite(a.time) ? a.time : -Infinity) || a.order - b.order)
      .map(({ entry, time }) => {
        const points = finiteValue(entry.points) ? Number(entry.points) : null;
        const date = Number.isFinite(time) ? new Date(time) : null;
        const amount = points === null ? '' : new Intl.NumberFormat('en-AU').format(Math.abs(points));
        return {
          title: String(entry.title || 'Points activity'),
          date: date ? DAY_LABEL.format(date) : '',
          month: date ? MONTH_LABEL.format(date) : 'Earlier',
          points,
          label: entry.label ? String(entry.label) : points === null ? '' : `${points > 0 ? '+' : points < 0 ? '−' : ''}${amount} ${Math.abs(points) === 1 ? 'point' : 'points'}`,
        };
      });
  }

  function filterRows(rows, filter) {
    if (filter === 'earned') return rows.filter((row) => row.points > 0);
    if (filter === 'redeemed') return rows.filter((row) => row.points < 0);
    return rows;
  }

  function groupByMonth(rows) {
    const groups = [];
    rows.forEach((row) => {
      const last = groups[groups.length - 1];
      if (last && last.month === row.month) last.rows.push(row);
      else groups.push({ month: row.month, rows: [row] });
    });
    return groups;
  }

  // Page loads never create a Wallet card, so rewards-page traffic never
  // reaches Okendo. Computers show the QR only for an existing card; otherwise
  // the Wallet buttons create the card on click and then swap to its QR.
  // Phones keep their one button, which creates the card (if needed) on tap.
  const WALLET_LOOKUP_TIMEOUT_MS = 15000;
  const WALLET_CREATE_TIMEOUT_MS = 45000;
  const WALLET_BUSY_MESSAGE = 'Wallet is busy right now. Please try again in a minute.';
  const WALLET_FAILED_MESSAGE = 'We couldn’t create your card. Please try again or contact us.';

  function initializeWallet(root) {
    const platform = walletPlatform(window.navigator?.userAgent, window.navigator?.maxTouchPoints);
    const wallet = root.querySelector('[data-loyalty-wallet]');
    const qr = wallet?.querySelector('[data-loyalty-wallet-qr]');
    root.dataset.walletPlatform = platform;
    if (!wallet) return () => {};
    const forms = Array.from(wallet.querySelectorAll('form.wallet-form'));
    const status = wallet.querySelector('[data-loyalty-wallet-status]');
    const cleanups = [];
    const listen = (target, type, handler) => {
      if (typeof target?.addEventListener !== 'function') return;
      target.addEventListener(type, handler);
      cleanups.push(() => target.removeEventListener(type, handler));
    };
    // The live region stays in the page (visually hidden while empty) so
    // screen readers announce new text.
    const say = (text = '') => { if (status) status.textContent = text; };
    // The buttons keep their look; only the small label changes while working.
    const setBusy = (active) => forms.forEach((form) => {
      const button = form.querySelector('button');
      if (!button) return;
      button.disabled = Boolean(active);
      if (active && form === active) button.setAttribute('aria-busy', 'true');
      else button.removeAttribute('aria-busy');
      const label = button.querySelector('.access-wallet-copy small');
      if (label) label.textContent = active && form === active ? 'Adding to' : 'Add to';
    });
    // Some browsers restore a script-disabled button on reload.
    setBusy(null);

    // A nested WebView can display the binary Apple pass as text. Explicit
    // Wallet taps always use app Rewards, even after “Stay on the website”.
    let appAttempt = null;
    const resetAppAttempt = () => {
      if (!appAttempt) return;
      window.clearTimeout(appAttempt.pollTimer);
      window.clearTimeout(appAttempt.timeoutTimer);
      appAttempt = null;
      setBusy(null);
    };
    const checkAppBridge = () => {
      const attempt = appAttempt;
      if (!attempt || attempt.dispatched) return;
      window.clearTimeout(attempt.pollTimer);
      if (window.Tapcart?.isInitialized !== true || typeof window.Tapcart?.action !== 'function') {
        attempt.pollTimer = window.setTimeout(checkAppBridge, APP_HANDOFF_POLL_MS);
        return;
      }
      attempt.dispatched = true;
      window.clearTimeout(attempt.timeoutTimer);
      attempt.timeoutTimer = window.setTimeout(attempt.fail, APP_HANDOFF_WAIT_MS);
      try {
        const result = window.Tapcart.action('screen/open', { destination: tapcartRewardsDestination(root) }, { onError: attempt.fail });
        result?.catch?.(attempt.fail);
      } catch {
        attempt.fail();
      }
    };
    const handoffWalletToApp = (event, form) => {
      if (!isTapcartApp() || window.Shopify?.designMode) return false;
      event.preventDefault();
      if (appAttempt) return true;
      const attempt = { dispatched: false, pollTimer: null, timeoutTimer: null };
      attempt.fail = () => {
        if (appAttempt !== attempt) return;
        resetAppAttempt();
        say('Open the app’s Rewards tab to add your Wallet card.');
      };
      appAttempt = attempt;
      setBusy(form);
      say('Opening Rewards in the app…');
      attempt.timeoutTimer = window.setTimeout(attempt.fail, APP_HANDOFF_BRIDGE_WAIT_MS);
      try {
        ensureTapcartBridge();
        checkAppBridge();
      } catch {
        attempt.fail();
      }
      return true;
    };
    listen(window, 'webbridge-loaded', checkAppBridge);
    listen(document, 'webbridge-loaded', checkAppBridge);
    listen(window, 'pageshow', (event) => {
      if (event?.persisted) { resetAppAttempt(); say(''); }
    });
    cleanups.push(resetAppAttempt);

    if (platform !== 'both' || !qr || isTapcartApp()) {
      if (platform === 'both') root.dataset.walletPlatform = 'buttons';
      // Prepare the selected card in the background, then open Wallet from
      // Rewards directly. A full form navigation leaves an extra button page
      // behind when Safari presents the pass sheet.
      let pending = null;
      let disposed = false;
      const reset = () => {
        pending?.abort();
        pending = null;
        setBusy(null);
      };
      forms.forEach((form) => listen(form, 'submit', async (event) => {
        if (handoffWalletToApp(event, form)) return;
        event.preventDefault();
        if (pending || disposed) return;
        const request = new AbortController();
        pending = request;
        setBusy(form);
        say('');
        const timeout = window.setTimeout(() => request.abort(), WALLET_CREATE_TIMEOUT_MS);
        try {
          const response = await fetch('/apps/ee-wallet/issue', {
            method: 'POST',
            credentials: 'same-origin',
            cache: 'no-store',
            signal: request.signal,
            headers: { 'Content-Type': 'application/x-www-form-urlencoded', Accept: 'application/json' },
            body: new URLSearchParams({ email: form.querySelector('[name="email"]').value, wallet: form.dataset.wallet, browser_handoff: '1' }),
          });
          let payload = null;
          try { payload = await response.json(); } catch { /* Non-JSON proxy error page. */ }
          if (disposed || pending !== request) return;
          if (!response.ok || !payload?.installUrl) {
            say(payload?.error || (response.status >= 500 || response.status === 429 ? WALLET_BUSY_MESSAGE : WALLET_FAILED_MESSAGE));
            return;
          }
          const target = new URL(payload.installUrl);
          const allowed = target.protocol === 'https:' && (form.dataset.wallet === 'google'
            ? target.hostname === 'pay.google.com' && target.pathname.startsWith('/gp/v/save/')
            : target.hostname === 'wallet.eliteelevensporting.com' && target.pathname.startsWith('/wallet/pass/') && target.pathname.endsWith('.pkpass'));
          if (!allowed) throw new Error('Invalid Wallet link');
          if (status) {
            say('If Wallet doesn’t open, ');
            const fallback = document.createElement('a');
            fallback.className = 'access-link';
            fallback.href = target.href;
            fallback.textContent = `open ${form.dataset.wallet === 'google' ? 'Google' : 'Apple'} Wallet`;
            status.append(fallback);
          }
          try { window.location.assign(target.href); } catch { /* Keep the manual link available. */ }
        } catch {
          if (!disposed && pending === request) say(WALLET_BUSY_MESSAGE);
        } finally {
          window.clearTimeout(timeout);
          if (pending === request) { pending = null; setBusy(null); }
        }
      }));
      listen(window, 'pageshow', (event) => { if (event?.persisted) reset(); });
      return () => {
        disposed = true;
        reset();
        cleanups.forEach((cleanup) => cleanup());
      };
    }

    const firstForm = forms[0];
    if (!firstForm) {
      wallet.remove();
      return () => {};
    }
    const controller = new AbortController();
    const link = qr.querySelector('a');
    const image = link.querySelector('img');
    let blobUrl;
    let disposed = false;
    let creating = false;
    const useButtons = () => { if (!disposed) root.dataset.walletPlatform = 'buttons'; };
    root.dataset.walletPlatform = 'qr-loading';
    listen(image, 'error', useButtons);

    const post = async (mode, timeoutMs) => {
      const request = new AbortController();
      const abort = () => request.abort();
      const timer = window.setTimeout(abort, timeoutMs);
      controller.signal.addEventListener('abort', abort, { once: true });
      try {
        const response = await fetch('/apps/ee-wallet/qr', {
          method: 'POST',
          credentials: 'same-origin',
          cache: 'no-store',
          signal: request.signal,
          headers: { 'Content-Type': 'application/x-www-form-urlencoded', Accept: 'application/json' },
          body: new URLSearchParams({ email: firstForm.querySelector('[name="email"]').value, mode }),
        });
        let payload = null;
        try { payload = await response.json(); } catch { /* Non-JSON proxy error page. */ }
        return { ok: response.ok, status: response.status, payload: payload || {} };
      } finally {
        window.clearTimeout(timer);
        controller.signal.removeEventListener('abort', abort);
      }
    };
    const showQr = (code) => {
      if (!code?.url || typeof code.svg !== 'string') throw new Error('Missing Wallet QR');
      const target = new URL(code.url);
      if (target.protocol !== 'https:' || target.hostname !== 'wallet.eliteelevensporting.com' || !target.pathname.startsWith('/wallet/install/')) throw new Error('Invalid Wallet link');
      if (blobUrl) URL.revokeObjectURL(blobUrl);
      blobUrl = URL.createObjectURL(new Blob([code.svg], { type: 'image/svg+xml' }));
      image.src = blobUrl;
      link.href = target.href;
      root.dataset.walletPlatform = 'both';
    };

    // An existing card shows its QR straight away; everyone else gets buttons.
    const lookup = async () => {
      try {
        const result = await post('lookup', WALLET_LOOKUP_TIMEOUT_MS);
        if (disposed) return;
        if (!result.ok || !result.payload.wallet) useButtons();
        else showQr(result.payload.wallet);
      } catch {
        useButtons();
      }
    };

    // Either button creates the card on a computer, then shows its QR.
    const create = async (event, form) => {
      if (handoffWalletToApp(event, form)) return;
      event.preventDefault();
      if (creating || disposed) return;
      creating = true;
      setBusy(form);
      say('');
      try {
        const result = await post('issue', WALLET_CREATE_TIMEOUT_MS);
        if (disposed) return;
        if (result.ok && result.payload.wallet) {
          showQr(result.payload.wallet);
        } else {
          useButtons();
          // Rate limits, timeouts and gateway errors: the card may be ready next click.
          say(result.status >= 500 || result.status === 429 || result.payload.code === 'busy' ? WALLET_BUSY_MESSAGE : WALLET_FAILED_MESSAGE);
        }
      } catch {
        if (disposed) return;
        // Timeouts and dropped connections: the card may be ready next visit.
        useButtons();
        say(WALLET_BUSY_MESSAGE);
      } finally {
        creating = false;
        if (!disposed) setBusy(null);
      }
    };
    forms.forEach((form) => listen(form, 'submit', (event) => create(event, form)));
    lookup();
    return () => {
      disposed = true;
      controller.abort();
      cleanups.forEach((cleanup) => cleanup());
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }

  function initializeHistory(root, suppliedClient) {
    const history = root.querySelector('[data-loyalty-history]');
    if (!history) return () => {};
    const endpoint = history.dataset.endpoint;
    const isTestLogin = Boolean(root.querySelector('[data-loyalty-test-member]'));
    const client = suppliedClient || (!endpoint && !isTestLogin ? window.EliteElevenLoyaltyOkendo?.createHistoryClient() : null);
    const groupsEl = history.querySelector('[data-loyalty-history-groups]');
    const empty = history.querySelector('[data-loyalty-history-empty]');
    const more = history.querySelector('[data-loyalty-history-more]');
    const status = history.querySelector('[data-loyalty-history-status]');
    const message = history.querySelector('[data-loyalty-history-message]');
    const retry = history.querySelector('[data-loyalty-history-retry]');
    const filters = Array.from(history.querySelectorAll('[data-filter]'));
    const filtersWrap = history.querySelector('.access-filters');
    let entries = [];
    let rows = [];
    let filter = 'all';
    let limit = HISTORY_PAGE;
    let nextUrl = null;
    let pending = null;
    let failedMore = false;
    let disposed = false;
    let refreshTimer = null;
    let hasPage = false;
    let drawnRows = null;
    let refreshQueued = false;

    const el = (tag, className, text) => {
      const node = document.createElement(tag);
      if (className) node.className = className;
      if (text !== undefined) node.textContent = text;
      return node;
    };
    const draw = () => {
      const visible = filterRows(rows, filter);
      const shown = visible.slice(0, limit);
      const key = JSON.stringify(shown);
      if (drawnRows !== key) {
        drawnRows = key;
        groupsEl.replaceChildren(...groupByMonth(shown).map((group) => {
          const wrap = el('div', 'access-history-group');
          wrap.append(el('p', 'access-history-month', group.month));
          const list = el('ul', 'access-history-list');
          group.rows.forEach((row) => {
            const item = el('li', 'access-history-item');
            const copy = el('div');
            copy.append(el('p', 'access-history-name', row.title));
            if (row.date) copy.append(el('p', 'access-history-date', row.date));
            item.append(copy);
            if (row.label) item.append(el('p', `access-history-points${row.points < 0 ? ' is-spent' : ''}`, row.label));
            list.append(item);
          });
          wrap.append(list);
          return wrap;
        }));
      }
      empty.hidden = visible.length > 0 || Boolean(pending) || !status.hidden;
      more.hidden = (visible.length <= limit && !nextUrl) || !status.hidden;
      more.disabled = Boolean(pending);
      if (pending) more.replaceChildren(loadingIndicator('Loading more points history…', true));
      else more.textContent = 'Show more';
      if (filtersWrap) filtersWrap.hidden = rows.length === 0;
      filters.forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.filter === filter)));
    };
    const load = async (append = false) => {
      if (disposed) return;
      if (pending) { if (!append) refreshQueued = true; return; }
      const controller = new AbortController();
      pending = controller;
      failedMore = append;
      history.hidden = false;
      history.setAttribute('aria-busy', 'true');
      status.hidden = append || hasPage;
      retry.hidden = true;
      message.replaceChildren(loadingIndicator('Loading your points history…'));
      draw();
      try {
        let page;
        if (endpoint) {
          const response = await window.fetch(endpoint, {
            credentials: 'same-origin', headers: { Accept: 'application/json' }, signal: controller.signal,
          });
          if (!response.ok) throw new Error('Points history unavailable');
          const data = await response.json();
          page = { entries: Array.isArray(data) ? data : data?.entries || data?.history || [], nextUrl: null };
        } else {
          if (!client) throw new Error('Points history unavailable');
          page = await client.loadPage({ nextUrl: append ? nextUrl : null, signal: controller.signal });
        }
        if (disposed) return;
        if (!append) entries = [];
        const existing = new Set(entries.map((entry) => entry.id).filter(Boolean));
        entries.push(...page.entries.filter((entry) => !entry.id || !existing.has(entry.id)));
        rows = historyRows(entries);
        nextUrl = page.nextUrl;
        hasPage = true;
        status.hidden = true;
      } catch (error) {
        if (disposed || error?.name === 'AbortError') return;
        if (error.status === 401 || error.status === 403) {
          entries = []; rows = []; nextUrl = null; hasPage = false;
        }
        status.hidden = false;
        message.textContent = hasPage
          ? 'Showing your previously loaded history. New activity is temporarily unavailable.'
          : 'We couldn’t load your points history. Please try again.';
        retry.hidden = false;
      } finally {
        pending = null;
        if (!disposed) {
          history.setAttribute('aria-busy', 'false');
          draw();
          if (refreshQueued) { refreshQueued = false; load(); }
        }
      }
    };
    const handleFilter = (event) => {
      filter = event.currentTarget.dataset.filter || 'all';
      limit = HISTORY_PAGE;
      draw();
    };
    const handleMore = () => {
      if (pending) return;
      limit += HISTORY_PAGE;
      if (filterRows(rows, filter).length < limit && nextUrl) load(true);
      else draw();
    };
    const handleRetry = () => load(failedMore);
    filters.forEach((button) => button.addEventListener('click', handleFilter));
    more.addEventListener('click', handleMore);
    retry.addEventListener('click', handleRetry);

    if (isTestLogin) {
      rows = historyRows(TEST_HISTORY);
      history.hidden = false;
      status.hidden = true;
      draw();
    } else {
      const saved = !endpoint && client?.readCache?.();
      if (Array.isArray(saved?.entries)) {
        entries = saved.entries; rows = historyRows(entries); nextUrl = saved.nextUrl;
        hasPage = true; history.hidden = false; status.hidden = true;
        draw();
      }
      load();
    }

    // Refresh only when the live balance changes after an action. No polling.
    const points = root.querySelector('[data-loyalty-points]');
    let balance = points?.textContent.trim() || '';
    const observer = !isTestLogin && points ? new MutationObserver(() => {
      const value = points.textContent.trim();
      if (!value || value === balance) return;
      const previous = balance;
      balance = value;
      if (!previous) return;
      if (refreshTimer !== null) window.clearTimeout(refreshTimer);
      refreshTimer = window.setTimeout(() => { refreshTimer = null; load(); }, 300);
    }) : null;
    if (observer) observer.observe(points, { childList: true, characterData: true, subtree: true });

    const cleanup = () => {
      disposed = true;
      pending?.abort();
      observer?.disconnect();
      if (refreshTimer !== null) window.clearTimeout(refreshTimer);
      filters.forEach((button) => button.removeEventListener('click', handleFilter));
      more.removeEventListener('click', handleMore);
      retry.removeEventListener('click', handleRetry);
    };
    cleanup.refresh = () => {
      if (refreshTimer !== null) { window.clearTimeout(refreshTimer); refreshTimer = null; }
      load();
    };
    return cleanup;
  }

  // Words for the convert slider: "1,250 of your 1,890 points" and "50 points = $1".
  function convertCopy(pointsText, amountText, balanceText) {
    const points = Number(String(pointsText || '').replace(/[^\d.]/g, ''));
    const match = String(amountText || '').match(/\$\s*([\d,.]+)/);
    const amount = match ? Number(match[1].replace(/,/g, '')) : NaN;
    const balance = String(balanceText || '').replace(/\s*points?\s*$/i, '').trim();
    const balanceNumber = Number(balance.replace(/[^\d.]/g, ''));
    const format = new Intl.NumberFormat('en-AU');
    // Only say "of your" when the balance covers it, so a sample never reads wrong.
    const ofBalance = balance && balanceNumber >= points;
    const line = points > 0
      ? ofBalance ? `${format.format(points)} of your ${balance} points` : `${format.format(points)} points`
      : '';
    // "50 points = $1" when that's a whole number, otherwise per 100 points
    // ("100 points = $6"), otherwise nothing.
    const whole = (number) => Number.isFinite(number) && number > 0 && Math.abs(number - Math.round(number)) < 0.01;
    const perDollar = points > 0 && amount > 0 ? points / amount : NaN;
    const perHundred = points > 0 && amount > 0 ? (amount * 100) / points : NaN;
    let rate = '';
    if (whole(perDollar)) rate = `${format.format(Math.round(perDollar))} points = $1`;
    else if (whole(perHundred)) rate = `100 points = $${format.format(Math.round(perHundred))}`;
    return { line, rate };
  }

  // Okendo's slider card only says "1250 points" and "$25". Hand the CSS the
  // fuller lines from the design; it falls back to Okendo's text without them.
  // Okendo's slider info is a screen-reader sentence ("Redeem 2,100 Points
  // for $126"), then the points, then the amount. Skip the hidden sentence.
  const ASSISTIVE_TEXT = '.u-assistive-hidden, [class*="screenreader" i], [class*="visually-hidden" i]';

  function initializeConvertCopy(root) {
    // The lines are set on the Redeem section, not the page, so updating them
    // while someone drags the slider only restyles that section.
    const redeem = root.querySelector('#redeem') || root;
    const sample = root.querySelector('[data-convert-sample-points]');
    const sampleText = sample?.textContent || '';
    let frame = 0;
    let shown = null;

    const update = () => {
      frame = 0;
      const balance = root.dataset.memberPoints || '';
      if (sample) {
        const copy = convertCopy(sample.dataset.convertSamplePoints, '$25', balance);
        const next = balance && copy.line ? copy.line : sampleText;
        if (sample.textContent !== next) sample.textContent = next;
      }

      const info = redeem.querySelector('.c-customisableActionCard-sliderInfo');
      const spans = info
        ? Array.from(info.children).filter((span) => span.tagName === 'SPAN' && !span.matches(ASSISTIVE_TEXT))
        : [];
      const copy = spans.length > 1
        ? convertCopy(spans[0].textContent, spans[spans.length - 1].textContent, balance)
        : { line: '', rate: '' };
      const key = `${copy.line}|${copy.rate}`;
      if (key === shown) return;
      shown = key;
      if (copy.line) {
        redeem.style.setProperty('--ee-convert-line', JSON.stringify(copy.line));
        redeem.style.setProperty('--ee-convert-rate', JSON.stringify(copy.rate));
        redeem.dataset.convertReady = '';
      } else {
        redeem.style.removeProperty('--ee-convert-line');
        redeem.style.removeProperty('--ee-convert-rate');
        delete redeem.dataset.convertReady;
      }
    };
    const schedule = () => {
      if (!frame) frame = window.requestAnimationFrame(update);
    };

    const observers = [];
    if (typeof MutationObserver === 'function') {
      const sourceObserver = new MutationObserver(schedule);
      redeem.querySelectorAll('[data-loyalty-source]').forEach((source) => {
        sourceObserver.observe(source, { childList: true, subtree: true, characterData: true });
      });
      const rootObserver = new MutationObserver(schedule);
      rootObserver.observe(root, { attributes: true, attributeFilter: ['data-member-points'] });
      observers.push(sourceObserver, rootObserver);
    }
    root.addEventListener('input', schedule);
    update();

    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      observers.forEach((observer) => observer.disconnect());
      root.removeEventListener('input', schedule);
    };
  }

  // ---------- Live top card ----------
  // The top membership card renders the paper gradient live instead of playing
  // its video. These are the exact colours and settings the tier videos and
  // posters were rendered with (keep them in sync if the artwork changes).
  // centers = each video's first-frame blob layout (kept for reference and for
  // re-rendering the videos). ink = logo colour, tint = colour cast.
  // RANDOM_CARD_LAYOUT: the live card gets a fresh blob layout on every visit,
  // so it's slightly different each time (Nick, 23 Oct). Set to false to start
  // from the video's layout instead (seamless still-to-live hand-off).
  const RANDOM_CARD_LAYOUT = true;
  const CARD_GRADIENTS = Object.freeze({
    1: { // Community: ice blue
      colors: '#7fa3c9,#eef3f8,#4d78a6,#b9d0e6', monochrome: false, ink: '#111111', tint: '',
      distortion: 0.55, swirl: 0.24, scale: 1.15, rotation: 110, speed: 0.32, edge: 0.6, film: 0.35,
      centers: '-0.6589,-0.3049,0.8919,-0.7491,-0.1888,-0.2981,0.0545,0.95,0.4019,0.1249,-0.5114,0.7425,-0.2986,-0.7366,-0.8764,0.2318',
    },
    2: { // Inner Circle: red
      colors: '#8e0a20,#d9d2c8,#c79aa1,#4c0410', monochrome: false, ink: '#ffffff', tint: '',
      distortion: 0.55, swirl: 0.24, scale: 1.15, rotation: 110, speed: 0.32, edge: 0.6, film: 0.35,
      centers: '-1.1,-0.4676,1.1,-0.5089,0.0619,0.269,0.3828,0.9194,0.9141,-0.0585,-0.5618,0.7232,0.2858,-0.825,-0.7245,0.3242',
    },
    3: { // VIP: cool grey (greyscale with a blue cast)
      colors: '#2c3137,#868d97,#dde1e5,#454b53', monochrome: true, ink: '#111111', tint: '#4f6075',
      distortion: 0.45, swirl: 0.2, scale: 1.1, rotation: 100, speed: 0.28, edge: 0.55, film: 0.18,
      centers: '-1.1,-0.6423,0.8169,-0.4553,-0.2392,0.2285,0.1181,0.8939,0.8449,0.1218,-0.3323,0.692,0.055,-0.9216,-0.9163,0.0776',
    },
    4: { // Elite: black and white
      colors: '#000000,#2e2e2e,#d6d6d6,#0e0e0e', monochrome: true, ink: '#ffffff', tint: '',
      distortion: 0.55, swirl: 0.24, scale: 1.15, rotation: 110, speed: 0.32, edge: 0.6, film: 0.35,
      centers: '-0.8987,-0.2665,0.8164,-0.5798,0.0762,-0.0232,0.3178,0.7808,0.6004,0.2354,-0.7092,0.6905,0.1509,-0.9032,-0.945,0.1413',
    },
  });

  // Live by default. A device that can't keep up, or reports a low battery,
  // falls back to the tier video (hardware video decoding is far cheaper);
  // no WebGL does the same. Reduced motion and data saver keep the still image.
  function initializeLiveCards(root, reduceMotion, onModeChange) {
    const cards = Array.from(root.querySelectorAll('[data-live-card]')).map((card) => ({
      card,
      layer: card.querySelector('[data-paper-gradient-card]'),
      slot: 1,
      controller: null,
    })).filter((item) => item.layer);
    const setMode = (item, mode) => {
      if (item.card.dataset.cardMode === mode) return;
      item.card.dataset.cardMode = mode;
      if (mode !== 'live') {
        item.card.classList.remove('is-live');
        item.controller?.destroy();
        item.controller = null;
      }
      onModeChange?.();
    };
    const apply = (item) => {
      const spec = CARD_GRADIENTS[item.slot] || CARD_GRADIENTS[1];
      item.layer.style.setProperty('--live-ink', spec.ink);
      item.layer.style.setProperty('--live-tint', spec.tint || 'transparent');
      return spec;
    };
    const mount = (item) => {
      const spec = apply(item);
      const api = window.MeshPaperGradient;
      if (destroyed) return;
      if (reduceMotion.matches || navigator.connection?.saveData) { setMode(item, 'poster'); return; }
      if (!api?.mount) {
        // paper-gradient.js may still be loading: wait for it (or the page load)
        // before falling back to the video.
        if (!item.waiting) {
          item.waiting = true;
          const retry = () => {
            document.removeEventListener('paper-gradient:loaded', retry);
            window.removeEventListener('load', retry);
            item.waiting = false;
            if (window.MeshPaperGradient?.mount) mount(item);
            else setMode(item, 'video');
          };
          document.addEventListener('paper-gradient:loaded', retry);
          window.addEventListener('load', retry);
        }
        return;
      }
      const { layer } = item;
      layer.dataset.colors = spec.colors;
      if (spec.monochrome) layer.dataset.monochrome = ''; else delete layer.dataset.monochrome;
      ['distortion', 'swirl', 'scale', 'rotation', 'speed', 'edge', 'film'].forEach((key) => {
        layer.dataset[key] = String(spec[key]);
      });
      if (RANDOM_CARD_LAYOUT) delete layer.dataset.centers;
      else layer.dataset.centers = spec.centers;
      // Listen before mounting: a still/unsupported decision can happen during mount.
      layer.addEventListener('paper-gradient:ready', () => {
        if (item.card.dataset.cardMode === 'live') item.card.classList.add('is-live');
      });
      layer.addEventListener('paper-gradient:still', (event) => {
        const reason = event.detail?.reason;
        setMode(item, reason === 'reduced-motion' || reason === 'save-data' ? 'poster' : 'video');
      });
      layer.addEventListener('paper-gradient:unsupported', () => setMode(item, 'video'));
      item.controller = api.mount(layer);
      if (!item.controller && item.card.dataset.cardMode === 'live') setMode(item, 'video');
    };
    let destroyed = false;
    cards.forEach(mount);
    const onMotion = () => {
      if (reduceMotion.matches) cards.forEach((item) => setMode(item, 'poster'));
    };
    reduceMotion.addEventListener('change', onMotion);
    return {
      setTier(slot) {
        cards.filter((item) => item.card.hasAttribute('data-loyalty-tier-card')).forEach((item) => {
          if (item.slot === slot) return;
          item.slot = slot;
          const spec = apply(item);
          // Keep this visit's blob layout when only the tier changes.
          const { centers, ...rest } = spec;
          item.controller?.update(RANDOM_CARD_LAYOUT ? rest : spec);
        });
      },
      destroy() {
        destroyed = true;
        reduceMotion.removeEventListener('change', onMotion);
        cards.forEach((item) => { item.controller?.destroy(); item.controller = null; });
      },
    };
  }

  // Keep the still visible until an eligible, on-screen card is playing.
  function initializeCardVideos(root, reduceMotion) {
    const videos = Array.from(root.querySelectorAll('[data-loyalty-card-video]'));
    if (!videos.length) return { setTier() {}, refresh() {}, destroy() {} };
    let destroyed = false;
    const states = videos.map((video) => ({
      video,
      // On a live card the video only plays as the fallback.
      host: video.closest('[data-live-card]'),
      member: video.hasAttribute('data-loyalty-card-video-member'),
      eligible: !video.hasAttribute('data-loyalty-card-video-member'),
      visible: typeof IntersectionObserver !== 'function',
      sourceUrl: video.querySelector('source')?.getAttribute('data-src') || '',
      loaded: false,
      starting: false,
      resumeAfterStart: false,
      failed: false,
    }));
    const shouldPlay = (state) => !destroyed && !reduceMotion.matches &&
      !document.hidden && state.eligible && state.visible && !state.failed &&
      (!state.host || state.host.dataset.cardMode === 'video');
    const conceal = (state) => state.video.classList.remove('is-playing');
    const update = (state) => {
      const { video } = state;
      if (!shouldPlay(state)) {
        state.resumeAfterStart = false;
        video.pause();
        conceal(state);
        return;
      }
      if (!state.loaded) {
        video.muted = true;
        video.querySelectorAll('source[data-src]').forEach((source) => {
          if (source.dataset.src) source.setAttribute('src', source.dataset.src);
        });
        state.loaded = true;
        video.load();
      }
      if (!video.paused) return;
      if (state.starting) {
        state.resumeAfterStart = true;
        return;
      }
      state.starting = true;
      try {
        Promise.resolve(video.play()).catch(() => {
          if (video.paused) conceal(state);
        }).finally(() => {
          state.starting = false;
          if (state.resumeAfterStart) {
            state.resumeAfterStart = false;
            update(state);
          }
        });
      } catch (_) {
        state.starting = false;
        conceal(state);
      }
    };
    const updateAll = () => states.forEach(update);
    states.forEach((state) => {
      state.onPlaying = () => {
        if (shouldPlay(state)) state.video.classList.add('is-playing');
        else update(state);
      };
      state.onPause = () => conceal(state);
      state.onError = () => { state.failed = true; update(state); };
      state.video.addEventListener('playing', state.onPlaying);
      state.video.addEventListener('pause', state.onPause);
      state.video.addEventListener('error', state.onError);
    });
    const observer = typeof IntersectionObserver === 'function'
      ? new IntersectionObserver((entries) => entries.forEach((entry) => {
        const state = states.find((item) => item.video === entry.target);
        if (!state) return;
        state.visible = entry.isIntersecting;
        update(state);
      }), { threshold: 0.01 })
      : null;
    states.forEach((state) => observer?.observe(state.video));
    reduceMotion.addEventListener('change', updateAll);
    document.addEventListener('visibilitychange', updateAll);
    updateAll();
    return {
      refresh: updateAll,
      setTier(slot) {
        states.filter((state) => state.member).forEach((state) => {
          const sourceUrl = state.video.querySelector('source')?.getAttribute('data-src') || '';
          if (sourceUrl !== state.sourceUrl) {
            state.sourceUrl = sourceUrl;
            state.loaded = false;
            state.failed = false;
            conceal(state);
          }
          state.eligible = Boolean(sourceUrl);
          update(state);
        });
      },
      destroy() {
        destroyed = true;
        observer?.disconnect();
        reduceMotion.removeEventListener('change', updateAll);
        document.removeEventListener('visibilitychange', updateAll);
        states.forEach((state) => {
          state.video.removeEventListener('playing', state.onPlaying);
          state.video.removeEventListener('pause', state.onPause);
          state.video.removeEventListener('error', state.onError);
          state.video.pause();
          conceal(state);
          if (state.loaded) {
            state.video.querySelectorAll('source[data-src]').forEach((source) => source.removeAttribute('src'));
            state.video.load();
          }
        });
      },
    };
  }

  // Tabs on the page's own markup (the signed-in rewards sections): click or
  // arrow keys pick a tab and show its panel.
  function initializeTabs(root) {
    const cleanups = [];
    root.querySelectorAll('[data-access-tabs]').forEach((list) => {
      const tabs = Array.from(list.querySelectorAll('[role="tab"]'));
      const panelFor = (tab) => root.querySelector(`#${tab.getAttribute('aria-controls')}`);
      const select = (chosen, focus) => {
        tabs.forEach((tab) => {
          const selected = tab === chosen;
          tab.setAttribute('aria-selected', String(selected));
          tab.tabIndex = selected ? 0 : -1;
          const panel = panelFor(tab);
          if (panel) panel.hidden = !selected;
        });
        if (focus) chosen.focus();
      };
      const handleClick = (event) => select(event.currentTarget, false);
      const handleKey = (event) => {
        const index = tabs.indexOf(event.currentTarget);
        const next = {
          ArrowRight: tabs[(index + 1) % tabs.length],
          ArrowLeft: tabs[(index - 1 + tabs.length) % tabs.length],
          Home: tabs[0],
          End: tabs[tabs.length - 1],
        }[event.key];
        if (!next) return;
        event.preventDefault();
        select(next, true);
      };
      tabs.forEach((tab) => {
        tab.addEventListener('click', handleClick);
        tab.addEventListener('keydown', handleKey);
      });
      cleanups.push(() => tabs.forEach((tab) => {
        tab.removeEventListener('click', handleClick);
        tab.removeEventListener('keydown', handleKey);
      }));
    });
    return () => cleanups.forEach((cleanup) => cleanup());
  }

  function initializeLoyaltyPage(root) {
    if (!root || instances.has(root)) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const stopAppHandoff = initializeAppHandoff(root);
    const stopLogout = initializeLogout(root);
    const tierHighlighter = createTierHighlighter(root);
    const cardVideos = initializeCardVideos(root, reduceMotion);
    const liveCards = initializeLiveCards(root, reduceMotion, () => cardVideos.refresh());
    const profile = createProfileRenderer(root, {
      onTier: tierHighlighter?.highlight,
      onCard: (slot) => {
        cardVideos.setTier(slot);
        liveCards.setTier(slot);
      },
    });
    // Test login has no Okendo profile to read; it renders sample data instead.
    const isTestLogin = Boolean(root.querySelector('[data-loyalty-test-member]'));
    const stopTestLogin = initializeTestLogin(root, profile);
    const isApi = root.hasAttribute('data-loyalty-api');
    const stopSourceStates = isApi ? () => {} : initializeSourceStates(root, profile?.showError);
    const video = root.querySelector('[data-loyalty-video]');
    if (video && !reduceMotion.matches) video.play()?.catch(() => {});
    const stopWallet = initializeWallet(root);
    const stopConvertCopy = isApi ? () => {} : initializeConvertCopy(root);
    const stopTabs = initializeTabs(root);
    const stopHistory = isApi ? () => {} : initializeHistory(root);
    const stopApi = isApi ? window.EliteElevenLoyaltyApiPage?.mount(root, {
      profile,
      initializeHistory: (client) => initializeHistory(root, client),
      convertCopy,
    }) : null;
    const bridge = isApi ? null : window.EliteElevenLoyaltyOkendo;
    const adapters = [];
    if (profile && bridge && !isTestLogin) {
      adapters.push(bridge.createAdapter(profile.source, {
        onProfile: profile.render,
        onProfileState: profile.setProfileState,
      }));
    }

    if (bridge) {
      root.querySelectorAll('[data-loyalty-source]').forEach((source) => {
        if (profile?.source === source) return;
        adapters.push(bridge.createAdapter(source));
      });
    }

    adapters.forEach((adapter) => adapter.start());

    instances.set(root, {
      destroy() {
        adapters.forEach((adapter) => adapter.destroy());
        tierHighlighter?.destroy();
        stopAppHandoff();
        stopLogout();
        stopWallet();
        stopSourceStates();
        stopHistory();
        stopApi?.();
        stopConvertCopy();
        stopTabs();
        cardVideos.destroy();
        liveCards.destroy();
        stopTestLogin();
        instances.delete(root);
      },
    });
  }

  function initializeAll(container = document) {
    container.querySelectorAll('[data-loyalty-page]').forEach(initializeLoyaltyPage);
  }

  function destroyAll(container) {
    container.querySelectorAll('[data-loyalty-page]').forEach((root) => {
      instances.get(root)?.destroy();
    });
  }

  window.EliteElevenLoyaltyPage = Object.freeze({
    helpers: Object.freeze({
      resolveTierIndex,
      isTapcartApp,
      initializeAppHandoff,
      tierGlow,
      spentFromProfile,
      pointsNumber,
      walletPlatform,
      initializeWallet,
      testProfile,
      initializeTestLogin,
      initializeHistory,
      historyRows,
      filterRows,
      groupByMonth,
      convertCopy,
      initializeSourceStates,
      initializeCardVideos,
      initializeLiveCards,
      CARD_GRADIENTS,
    }),
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeAll());
  } else {
    initializeAll();
  }

  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroyAll(event.target));
})();
